import {
  AlertTriangle,
  Baseline,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Columns3,
  Eye,
  EyeOff,
  Filter,
  Flag,
  GitBranch,
  GripVertical,
  HelpCircle,
  Home,
  Kanban,
  Languages,
  Link,
  ListTree,
  LogOut,
  Maximize2,
  Minimize2,
  Move,
  Plus,
  RefreshCw,
  Rows3,
  Save,
  Search,
  Send,
  ShieldCheck,
  Undo2,
  SlidersHorizontal,
  UserCircle,
  UserPlus,
  XCircle
} from "lucide-react";
import { createContext, FormEvent, useContext, useEffect, useMemo, useRef, useState } from "react";

type Role = "admin" | "editor" | "partner";
type Language = "en" | "zh";

type User = {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  is_active: boolean;
};

type Project = {
  id: number;
  slug: string;
  name: string;
  description?: string;
  status: string;
  start_date: string;
  task_count?: number;
  done_count?: number;
  critical_count?: number;
};

type WorkTask = {
  id: number;
  project_id: number;
  parent_id: number | null;
  owner_user_id: number | null;
  calendar_id: number | null;
  code: string | null;
  title: string;
  description: string | null;
  item_type: "phase" | "task" | "milestone";
  category: string;
  status: string;
  priority: string;
  position: number;
  start_date: string | null;
  finish_date: string | null;
  duration_days: number;
  scheduling_mode: "auto" | "manual";
  percent_complete: number;
  partner_visible: boolean;
  send_to_taskplanner: boolean;
  critical: boolean;
  slack_days: number | null;
  delay_impact: string | null;
  budget_cost: number | null;
  actual_cost: number | null;
  taskplanner_sync?: SyncRecord | null;
  baseline?: { start_date: string | null; finish_date: string | null } | null;
};

type Dependency = {
  id: number;
  predecessor_id: number;
  successor_id: number;
  dependency_type: string;
  lag_days: number;
};

type Resource = {
  id: number;
  name: string;
  kind: string;
  capacity_percent: number;
  user_id: number | null;
};

type Calendar = {
  id: number;
  name: string;
  working_weekdays: string;
  notes: string | null;
};

type Risk = {
  id: number;
  title: string;
  description: string | null;
  severity: string;
  likelihood: string;
  status: string;
  due_date: string | null;
};

type Decision = {
  id: number;
  title: string;
  context: string | null;
  decision: string | null;
  status: string;
};

type Proposal = {
  id: number;
  task_id: number;
  proposer_user_id: number | null;
  field_changes: Record<string, unknown>;
  reason: string | null;
  status: string;
  review_note: string | null;
  created_at: string;
};

type BaselineRecord = {
  id: number;
  name: string;
  notes: string | null;
  captured_at: string;
  item_count: number;
};

type SyncRecord = {
  last_status: string;
  last_error: string | null;
  synced_at: string | null;
};

type Workspace = {
  project: Project;
  tasks: WorkTask[];
  dependencies: Dependency[];
  resources: Resource[];
  calendars: Calendar[];
  assignments: { id: number; task_id: number; resource_id: number; allocation_percent: number }[];
  risks: Risk[];
  decisions: Decision[];
  meetings: { id: number; title: string; held_on: string | null; summary: string | null; action_notes: string | null }[];
  comments: { id: number; task_id: number | null; body: string; created_at: string }[];
  evidence: { id: number; task_id: number | null; title: string; url: string; notes: string | null }[];
  baselines: BaselineRecord[];
  proposals: Proposal[];
  users: User[];
};

type Tab = "risks" | "decisions" | "proposals" | "resources" | "baselines" | "taskplanner" | "people";
type WorkspaceView = "timeline" | "board";
type DependencyMode = "selected" | "all" | "off";
type BoardGroupBy = "phase" | "status" | "owner" | "category" | "priority";
type BoardDensity = "comfortable" | "compact";
type BoardDueWindow = "all" | "overdue" | "next_7" | "next_30" | "no_finish";
type BoardFilters = {
  ownerId: string;
  status: string;
  category: string;
  search: string;
  dueWindow: BoardDueWindow;
  criticalOnly: boolean;
  partnerVisibleOnly: boolean;
};
type BoardPreferences = {
  groupBy: BoardGroupBy;
  density: BoardDensity;
  filters: BoardFilters;
};
type BoardPreferencesPatch = Partial<Omit<BoardPreferences, "filters">> & { filters?: Partial<BoardFilters> };
type BoardMovePayload = {
  parent_id?: number | null;
  position?: number;
  status?: string;
  confirm_parent_change?: boolean;
};
type BoardBucket = {
  id: string;
  title: string;
  subtitle: string;
  value: string | number | null;
  parentId?: number | null;
  tasks: WorkTask[];
};
type TaskSelectOptions = { toggle?: boolean; range?: boolean; preserveSelection?: boolean };
type TaskDateChange = { taskId: number; body: Partial<WorkTask> };
type UndoAction = { label: string; run: () => Promise<unknown> };
type TourStep = { target: string; title: string; body: string };

const helpTourStorageKey = "manufacturing_help_tour_dismissed";
const tourSteps: TourStep[] = [
  { target: "view-switch", title: "Timeline and Board", body: "Switch between Timeline and Board without changing the project data." },
  { target: "timeline-fullscreen", title: "Timeline fullscreen", body: "Use fullscreen for meetings; the task list stays visible so rows still make sense." },
  { target: "timeline-home", title: "Home", body: "Jump back to the project start and the first task rows when the chart is scrolled away." },
  { target: "recalculate", title: "Recalculate", body: "Refresh downstream dates after dependency, duration, or schedule edits." },
  { target: "help", title: "Help", body: "Open Help any time for timeline colours, dependency lines, dragging, and board basics." }
];

const categories = ["planning", "purchasing", "factory", "firmware", "qa", "decision", "commercial", "shipping"];
const statuses = ["not_started", "active", "blocked", "done", "cancelled"];
const priorities = ["low", "medium", "high", "urgent"];

const zhTranslations: Record<string, string> = {
  "Manufacturing Projects": "\u5236\u9020\u9879\u76ee",
  "Internal schedule and operations workspace": "\u5185\u90e8\u8ba1\u5212\u4e0e\u8fd0\u8425\u5de5\u4f5c\u533a",
  "Username": "\u7528\u6237\u540d",
  "Password": "\u5bc6\u7801",
  "Sign in": "\u767b\u5f55",
  "Sign out": "\u9000\u51fa\u767b\u5f55",
  "Incorrect username or password": "\u7528\u6237\u540d\u6216\u5bc6\u7801\u4e0d\u6b63\u786e",
  "Login failed": "\u767b\u5f55\u5931\u8d25",
  "Timeline": "\u65f6\u95f4\u7ebf",
  "Board": "\u770b\u677f",
  "Tasks": "\u4efb\u52a1",
  "Recalc": "\u91cd\u7b97",
  "Recalculate schedule": "\u91cd\u65b0\u8ba1\u7b97\u8ba1\u5212",
  "Show timeline help": "\u663e\u793a\u65f6\u95f4\u7ebf\u5e2e\u52a9",
  "Quick tour": "\u5feb\u901f\u5bfc\u89c8",
  "Would you like a quick tour?": "\u8981\u4e0d\u8981\u5148\u770b\u4e00\u4e2a\u5feb\u901f\u5bfc\u89c8\uff1f",
  "The tour highlights the main controls Dylan mentioned: fullscreen, Home, Timeline/Board, Recalc, and Help.": "\u5bfc\u89c8\u4f1a\u9ad8\u4eae Dylan \u63d0\u5230\u7684\u4e3b\u8981\u63a7\u4ef6\uff1a\u5168\u5c4f\u3001Home\u3001\u65f6\u95f4\u7ebf/\u770b\u677f\u3001\u91cd\u7b97\u548c\u5e2e\u52a9\u3002",
  "Start tour": "\u5f00\u59cb\u5bfc\u89c8",
  "Not now": "\u6682\u65f6\u4e0d\u8981",
  "Do not show again": "\u4e0d\u518d\u663e\u793a",
  "Back": "\u4e0a\u4e00\u6b65",
  "Next": "\u4e0b\u4e00\u6b65",
  "Skip": "\u8df3\u8fc7",
  "Step": "\u6b65\u9aa4",
  "Timeline and Board": "\u65f6\u95f4\u7ebf\u548c\u770b\u677f",
  "Switch between Timeline and Board without changing the project data.": "\u5728\u65f6\u95f4\u7ebf\u548c\u770b\u677f\u4e4b\u95f4\u5207\u6362\uff0c\u4e0d\u4f1a\u6539\u53d8\u9879\u76ee\u6570\u636e\u3002",
  "Timeline fullscreen": "\u65f6\u95f4\u7ebf\u5168\u5c4f",
  "Use fullscreen for meetings; the task list stays visible so rows still make sense.": "\u4f1a\u8bae\u65f6\u53ef\u4f7f\u7528\u5168\u5c4f\uff1b\u4efb\u52a1\u5217\u8868\u4f1a\u4fdd\u6301\u53ef\u89c1\uff0c\u65b9\u4fbf\u5bf9\u5e94\u884c\u5185\u5bb9\u3002",
  "Home": "Home",
  "Return timeline to project start": "\u8fd4\u56de\u9879\u76ee\u5f00\u59cb\u4f4d\u7f6e",
  "Timeline reset to project start": "\u65f6\u95f4\u7ebf\u5df2\u56de\u5230\u9879\u76ee\u5f00\u59cb\u4f4d\u7f6e",
  "Jump back to the project start and the first task rows when the chart is scrolled away.": "\u5f53\u56fe\u8868\u6eda\u8fdc\u540e\uff0c\u4e00\u952e\u56de\u5230\u9879\u76ee\u5f00\u59cb\u548c\u6700\u4e0a\u65b9\u4efb\u52a1\u884c\u3002",
  "Recalculate": "\u91cd\u65b0\u8ba1\u7b97",
  "Refresh downstream dates after dependency, duration, or schedule edits.": "\u4fee\u6539\u4f9d\u8d56\u3001\u5de5\u671f\u6216\u65e5\u671f\u540e\uff0c\u5237\u65b0\u540e\u7eed\u65e5\u671f\u3002",
  "Help": "\u5e2e\u52a9",
  "Open Help any time for timeline colours, dependency lines, dragging, and board basics.": "\u968f\u65f6\u6253\u5f00\u5e2e\u52a9\u67e5\u770b\u989c\u8272\u3001\u4f9d\u8d56\u7ebf\u3001\u62d6\u52a8\u548c\u770b\u677f\u57fa\u7840\u8bf4\u660e\u3002",
  "Navigation": "\u5bfc\u822a",
  "Timeline controls": "\u65f6\u95f4\u7ebf\u63a7\u4ef6",
  "Dependencies and dates": "\u4f9d\u8d56\u548c\u65e5\u671f",
  "The Home button resets horizontal and vertical timeline scroll to the project start.": "Home \u6309\u94ae\u4f1a\u628a\u65f6\u95f4\u7ebf\u6a2a\u5411\u548c\u7eb5\u5411\u6eda\u52a8\u91cd\u7f6e\u5230\u9879\u76ee\u5f00\u59cb\u4f4d\u7f6e\u3002",
  "Timeline fullscreen keeps the task list visible so meeting views still have row context.": "\u65f6\u95f4\u7ebf\u5168\u5c4f\u4f1a\u4fdd\u7559\u4efb\u52a1\u5217\u8868\uff0c\u4f1a\u8bae\u67e5\u770b\u65f6\u4ecd\u80fd\u77e5\u9053\u6bcf\u4e00\u884c\u662f\u4ec0\u4e48\u4efb\u52a1\u3002",
  "Collapse the lower panel when risks, decisions, or resources are not needed.": "\u4e0d\u9700\u8981\u98ce\u9669\u3001\u51b3\u7b56\u6216\u8d44\u6e90\u65f6\uff0c\u53ef\u4ee5\u6536\u8d77\u4e0b\u65b9\u9762\u677f\u3002",
  "Board view shows the same project tasks as cards; use grouping and search to scan ownership or blockers.": "\u770b\u677f\u663e\u793a\u540c\u4e00\u6279\u9879\u76ee\u4efb\u52a1\u5361\u7247\uff0c\u53ef\u7528\u5206\u7ec4\u548c\u641c\u7d22\u67e5\u770b\u8d1f\u8d23\u4eba\u6216\u963b\u585e\u9879\u3002",
  "Schedule recalculated": "\u8ba1\u5212\u5df2\u91cd\u65b0\u8ba1\u7b97",
  "Task dates updated": "\u4efb\u52a1\u65e5\u671f\u5df2\u66f4\u65b0",
  "Task saved": "\u4efb\u52a1\u5df2\u4fdd\u5b58",
  "Board updated": "\u770b\u677f\u5df2\u66f4\u65b0",
  "Proposal submitted": "\u5efa\u8bae\u5df2\u63d0\u4ea4",
  "Clear": "\u6e05\u9664",
  "selected": "\u5df2\u9009\u62e9",
  "planning": "\u8ba1\u5212",
  "purchasing": "\u91c7\u8d2d",
  "factory": "\u5de5\u5382",
  "firmware": "\u56fa\u4ef6",
  "qa": "\u8d28\u68c0",
  "decision": "\u51b3\u7b56",
  "commercial": "\u5546\u52a1",
  "shipping": "\u7269\u6d41",
  "admin": "\u7ba1\u7406\u5458",
  "editor": "\u7f16\u8f91",
  "partner": "\u5408\u4f5c\u4f19\u4f34",
  "not started": "\u672a\u5f00\u59cb",
  "not_started": "\u672a\u5f00\u59cb",
  "active": "\u8fdb\u884c\u4e2d",
  "blocked": "\u963b\u585e",
  "done": "\u5b8c\u6210",
  "cancelled": "\u5df2\u53d6\u6d88",
  "low": "\u4f4e",
  "medium": "\u4e2d",
  "high": "\u9ad8",
  "urgent": "\u7d27\u6025",
  "critical": "\u5173\u952e",
  "Title": "\u6807\u9898",
  "Status": "\u72b6\u6001",
  "Category": "\u7c7b\u522b",
  "Priority": "\u4f18\u5148\u7ea7",
  "Start": "\u5f00\u59cb",
  "Finish": "\u5b8c\u6210",
  "Duration": "\u5de5\u671f",
  "Complete %": "\u5b8c\u6210 %",
  "Scheduling": "\u8c03\u5ea6\u65b9\u5f0f",
  "Owner": "\u8d1f\u8d23\u4eba",
  "Description": "\u63cf\u8ff0",
  "Partner visible": "\u5408\u4f5c\u4f19\u4f34\u53ef\u89c1",
  "Task Planner": "\u4efb\u52a1\u8ba1\u5212\u5668",
  "Include weekends / elapsed time": "\u5305\u542b\u5468\u672b / \u81ea\u7136\u65e5",
  "No task selected": "\u672a\u9009\u62e9\u4efb\u52a1",
  "Unassigned": "\u672a\u5206\u914d",
  "No start": "\u65e0\u5f00\u59cb\u65e5\u671f",
  "No finish": "\u65e0\u5b8c\u6210\u65e5\u671f",
  "No code": "\u65e0\u7f16\u53f7",
  "No cards": "\u65e0\u5361\u7247",
  "All owners": "\u6240\u6709\u8d1f\u8d23\u4eba",
  "All status": "\u6240\u6709\u72b6\u6001",
  "All categories": "\u6240\u6709\u7c7b\u522b",
  "All dates": "\u6240\u6709\u65e5\u671f",
  "Overdue": "\u5df2\u903e\u671f",
  "Next 7 days": "\u672a\u6765 7 \u5929",
  "Next 30 days": "\u672a\u6765 30 \u5929",
  "Comfort": "\u8212\u9002",
  "Compact": "\u7d27\u51d1",
  "Critical": "\u5173\u952e",
  "Partner": "\u5408\u4f19\u4eba",
  "Language": "\u8bed\u8a00",
  "Search cards": "\u641c\u7d22\u5361\u7247",
  "cards": "\u5f20\u5361\u7247",
  "to": "\u5230",
  "Phase": "\u9636\u6bb5",
  "Commercial and Contract": "\u5546\u52a1\u4e0e\u5408\u540c",
  "Contract signed and first payment received": "\u5408\u540c\u5df2\u7b7e\u7f72\u5e76\u6536\u5230\u9996\u4ed8\u6b3e",
  "Pay remaining manufacturing balance": "\u652f\u4ed8\u5269\u4f59\u751f\u4ea7\u6b3e\u9879",
  "Grandtop confirms remaining payment received": "Grandtop \u786e\u8ba4\u6536\u5230\u4f59\u6b3e",
  "Confirm final 1,860 device configuration": "\u786e\u8ba4 1,860 \u53f0\u8bbe\u5907\u7684\u6700\u7ec8\u914d\u7f6e",
  "Dylan Visit": "Dylan \u8bbf\u5382",
  "Confirm visit prerequisites and trip timing": "\u786e\u8ba4\u8bbf\u5382\u524d\u63d0\u548c\u884c\u7a0b\u65f6\u95f4",
  "Dylan visit in Shenzhen": "Dylan \u6df1\u5733\u8bbf\u5382",
  "Order Parts": "\u8ba2\u8d2d\u96f6\u4ef6",
  "Load cells - confirm type, supplier, and ship plan": "\u79f0\u91cd\u4f20\u611f\u5668 - \u786e\u8ba4\u578b\u53f7\u3001\u4f9b\u5e94\u5546\u548c\u53d1\u8d27\u8ba1\u5212",
  "Load cells - confirm cost and order 3,720 units": "\u79f0\u91cd\u4f20\u611f\u5668 - \u786e\u8ba4\u6210\u672c\u5e76\u8ba2\u8d2d 3,720 \u4ef6",
  "Membranes - confirm Grandtop stock": "\u8584\u819c - \u786e\u8ba4 Grandtop \u5e93\u5b58",
  "PCB boards - order with two-week lead time": "PCB \u677f - \u6309\u4e24\u5468\u4ea4\u671f\u4e0b\u5355",
  "Book PCBA assembly slot with Grandtop management": "\u4e0e Grandtop \u7ba1\u7406\u5c42\u9884\u7ea6 PCBA \u7ec4\u88c5\u65f6\u6bb5",
  "PCBA assembly": "PCBA \u7ec4\u88c5",
  "Cables - confirm stock or order/make": "\u7ebf\u7f06 - \u786e\u8ba4\u5e93\u5b58\u6216\u5b89\u6392\u8ba2\u8d2d/\u5236\u4f5c",
  "ESP32-S2 - confirm stock": "ESP32-S2 - \u786e\u8ba4\u5e93\u5b58",
  "Other components - confirm stock": "\u5176\u4ed6\u5143\u5668\u4ef6 - \u786e\u8ba4\u5e93\u5b58",
  "Plastics": "\u5851\u6599\u4ef6",
  "Confirm enclosure mix and model split": "\u786e\u8ba4\u5916\u58f3\u7ec4\u5408\u548c\u578b\u53f7\u5206\u914d",
  "Decide plastic material: ABS vs polypropylene": "\u51b3\u5b9a\u5851\u6599\u6750\u6599\uff1aABS \u6216 PP",
  "Confirm UV protection additive": "\u786e\u8ba4 UV \u9632\u62a4\u6dfb\u52a0\u5242",
  "Confirm thermal additive and thermal properties": "\u786e\u8ba4\u5bfc\u70ed\u6dfb\u52a0\u5242\u548c\u70ed\u6027\u80fd",
  "Confirm colour: grey vs black": "\u786e\u8ba4\u989c\u8272\uff1a\u7070\u8272\u6216\u9ed1\u8272",
  "Request outer box, lid, and tray samples": "\u8981\u6c42\u5916\u7bb1\u3001\u76d6\u5b50\u548c\u6258\u76d8\u6837\u54c1",
  "Supplier sample injection run": "\u4f9b\u5e94\u5546\u6837\u54c1\u6ce8\u5851\u8bd5\u4ea7",
  "Ashley/Grandtop validate plastic samples": "Ashley/Grandtop \u9a8c\u8bc1\u5851\u6599\u6837\u54c1",
  "Approve plastic sample quality and warping": "\u6279\u51c6\u5851\u6599\u6837\u54c1\u8d28\u91cf\u548c\u53d8\u5f62\u60c5\u51b5",
  "Full plastic injection run": "\u5851\u6599\u4ef6\u5168\u91cf\u6ce8\u5851",
  "Reliability QA and Documentation": "\u53ef\u9760\u6027\u8d28\u68c0\u4e0e\u6587\u6863",
  "Drop test after plastics are available": "\u5851\u6599\u4ef6\u5230\u4f4d\u540e\u8fdb\u884c\u8dcc\u843d\u6d4b\u8bd5",
  "Collect ROHS certificates and material datasheets": "\u6536\u96c6 ROHS \u8bc1\u4e66\u548c\u6750\u6599\u6570\u636e\u8868",
  "Temperature-related tests during Dylan visit": "Dylan \u8bbf\u5382\u671f\u95f4\u7684\u6e29\u5ea6\u76f8\u5173\u6d4b\u8bd5",
  "Vibration tests during Dylan visit": "Dylan \u8bbf\u5382\u671f\u95f4\u7684\u632f\u52a8\u6d4b\u8bd5",
  "ISTA-2A transportation test": "ISTA-2A \u8fd0\u8f93\u6d4b\u8bd5",
  "Pre-production": "\u9884\u751f\u4ea7",
  "Update assembly procedure document": "\u66f4\u65b0\u7ec4\u88c5\u6d41\u7a0b\u6587\u6863",
  "Test current QA procedure": "\u6d4b\u8bd5\u5f53\u524d QA \u6d41\u7a0b",
  "Investigate new software QA procedure": "\u8bc4\u4f30\u65b0\u7684\u8f6f\u4ef6 QA \u6d41\u7a0b",
  "Define quality plan": "\u5236\u5b9a\u8d28\u91cf\u8ba1\u5212",
  "Production": "\u751f\u4ea7",
  "Assembly and QA of 200 units during Dylan's visit": "Dylan \u8bbf\u5382\u671f\u95f4\u7ec4\u88c5\u5e76\u8d28\u68c0 200 \u53f0",
  "Assembly and QA of remaining 1,660 units": "\u7ec4\u88c5\u5e76\u8d28\u68c0\u5269\u4f59 1,660 \u53f0",
  "Packaging": "\u5305\u88c5",
  "Review packaging designs": "\u5ba1\u67e5\u5305\u88c5\u8bbe\u8ba1",
  "Confirm cardboard material type": "\u786e\u8ba4\u7eb8\u677f\u6750\u6599\u7c7b\u578b",
  "Confirm protective cardboard corners": "\u786e\u8ba4\u7eb8\u677f\u62a4\u89d2",
  "Validate packaging construction": "\u9a8c\u8bc1\u5305\u88c5\u7ed3\u6784",
  "Confirm branding on box": "\u786e\u8ba4\u5916\u7bb1\u54c1\u724c\u6807\u8bc6",
  "Order packaging materials": "\u8ba2\u8d2d\u5305\u88c5\u6750\u6599",
  "Shipping": "\u7269\u6d41",
  "Organise shipping plan": "\u5b89\u6392\u53d1\u8d27\u8ba1\u5212",
  "UK shipping": "\u82f1\u56fd\u8fd0\u8f93",
  "UK customs": "\u82f1\u56fd\u6e05\u5173",
  "AUS shipping": "\u6fb3\u5927\u5229\u4e9a\u8fd0\u8f93",
  "AUS customs": "\u6fb3\u5927\u5229\u4e9a\u6e05\u5173",
  "Administrative and Release Gates": "\u884c\u653f\u4e0e\u53d1\u5e03\u8282\u70b9",
  "Assembly instructions final copy": "\u7ec4\u88c5\u8bf4\u660e\u6700\u7ec8\u7248",
  "FCC and CE certification record": "FCC \u548c CE \u8ba4\u8bc1\u8bb0\u5f55",
  "Labelling": "\u6807\u7b7e",
  "Firmware sign-off": "\u56fa\u4ef6\u7b7e\u6838",
  "QA dashboard testing": "QA \u770b\u677f\u6d4b\u8bd5",
  "Hand scanner working": "\u624b\u6301\u626b\u63cf\u5668\u53ef\u7528",
  "1,860 Device Run": "1,860 \u53f0\u8bbe\u5907\u751f\u4ea7\u8fd0\u884c",
  "Motion Sensor Manufacturing": "\u8fd0\u52a8\u4f20\u611f\u5668\u5236\u9020",
  "New Product Manufacturing": "\u65b0\u4ea7\u54c1\u5236\u9020",
  "Version 1.1 Manufacturing Plan (MPP Source)": "1.1 \u7248\u5236\u9020\u8ba1\u5212\uff08MPP \u6765\u6e90\uff09",
  "risks": "\u98ce\u9669",
  "decisions": "\u51b3\u7b56",
  "proposals": "\u5efa\u8bae",
  "resources": "\u8d44\u6e90",
  "baselines": "\u57fa\u7ebf",
  "taskplanner": "\u4efb\u52a1\u8ba1\u5212",
  "people": "\u4eba\u5458",
  "Dismiss notification": "\u5173\u95ed\u901a\u77e5",
  "Drag to resize task list": "\u62d6\u52a8\u4ee5\u8c03\u6574\u4efb\u52a1\u5217\u8868\u5bbd\u5ea6",
  "Drag to resize task inspector": "\u62d6\u52a8\u4ee5\u8c03\u6574\u4efb\u52a1\u8be6\u60c5\u5bbd\u5ea6",
  "Drag to resize lower panel": "\u62d6\u52a8\u4ee5\u8c03\u6574\u4e0b\u65b9\u9762\u677f\u9ad8\u5ea6",
  "Expand lower panel": "\u5c55\u5f00\u4e0b\u65b9\u9762\u677f",
  "Collapse lower panel": "\u6536\u8d77\u4e0b\u65b9\u9762\u677f",
  "Task colours follow manufacturing category": "\u4efb\u52a1\u989c\u8272\u6309\u5236\u9020\u7c7b\u522b\u663e\u793a",
  "Timeline Help": "\u65f6\u95f4\u7ebf\u5e2e\u52a9",
  "Close help": "\u5173\u95ed\u5e2e\u52a9",
  "Phase roll-up bars summarize child tasks.": "\u9636\u6bb5\u6c47\u603b\u6761\u4f1a\u6c47\u603b\u5176\u5b50\u4efb\u52a1\u3002",
  "Task bars are colour-coded by category.": "\u4efb\u52a1\u6761\u6309\u7c7b\u522b\u7528\u4e0d\u540c\u989c\u8272\u6807\u8bb0\u3002",
  "Red bars are on the current critical path.": "\u7ea2\u8272\u6761\u4ee3\u8868\u5f53\u524d\u5173\u952e\u8def\u5f84\u3002",
  "Diamonds are milestones.": "\u83f1\u5f62\u8868\u793a\u91cc\u7a0b\u7891\u3002",
  "Chain icons mark dependency-linked tasks.": "\u94fe\u63a5\u56fe\u6807\u8868\u793a\u5b58\u5728\u4f9d\u8d56\u5173\u7cfb\u7684\u4efb\u52a1\u3002",
  "Drag a task bar to move dates; drag its left or right edge to resize.": "\u62d6\u52a8\u4efb\u52a1\u6761\u53ef\u79fb\u52a8\u65e5\u671f\uff1b\u62d6\u52a8\u5de6\u53f3\u8fb9\u7f18\u53ef\u8c03\u6574\u65f6\u957f\u3002",
  "Use the task inspector's weekend checkbox to switch between elapsed days and workdays.": "\u4f7f\u7528\u4efb\u52a1\u8be6\u60c5\u4e2d\u7684\u5468\u672b\u9009\u9879\u5728\u81ea\u7136\u65e5\u548c\u5de5\u4f5c\u65e5\u4e4b\u95f4\u5207\u6362\u3002",
  "Drag the vertical dividers or use fullscreen when the plan needs more room.": "\u9700\u8981\u66f4\u591a\u7a7a\u95f4\u65f6\u53ef\u62d6\u52a8\u7ad6\u5411\u5206\u9694\u6761\u6216\u4f7f\u7528\u5168\u5c4f\u3002",
  "Dependency lines show task links; press H to highlight the selected task path.": "依赖线显示任务之间的连接；按 H 可高亮所选任务路径。",
  "Path": "路径",
  "Links": "链接",
  "Selected": "所选",
  "Off": "关闭",
  "Highlight selected dependency path (H)": "高亮所选依赖路径 (H)",
  "Collapse to phases": "折叠到阶段",
  "Collapse this branch": "折叠此分支",
  "Expand this branch": "展开此分支",
  "Show work packages": "显示工作包",
  "Show all tasks": "显示所有任务",
  "Dependency selected": "已选择依赖",
  "rolled up": "已汇总",
  "planner": "\u8ba1\u5212\u5668",
  "internal": "\u5185\u90e8",
  "auto": "\u81ea\u52a8",
  "manual": "\u624b\u52a8",
  "Include weekends uses elapsed calendar days; excluding weekends uses normal workdays.": "\u5305\u542b\u5468\u672b\u8868\u793a\u6309\u81ea\u7136\u65e5\u8ba1\uff1b\u4e0d\u5305\u542b\u5219\u6309\u6b63\u5e38\u5de5\u4f5c\u65e5\u8ba1\u3002",
  "Save": "\u4fdd\u5b58",
  "Reason": "\u539f\u56e0",
  "Propose": "\u63d0\u4ea4\u5efa\u8bae",
  "Risk added": "\u98ce\u9669\u5df2\u6dfb\u52a0",
  "Decision added": "\u51b3\u7b56\u5df2\u6dfb\u52a0",
  "Proposal approved": "\u5efa\u8bae\u5df2\u6279\u51c6",
  "Proposal rejected": "\u5efa\u8bae\u5df2\u62d2\u7edd",
  "Baseline captured": "\u57fa\u7ebf\u5df2\u6355\u83b7",
  "Task Planner sync completed": "\u4efb\u52a1\u8ba1\u5212\u540c\u6b65\u5df2\u5b8c\u6210",
  "User created": "\u7528\u6237\u5df2\u521b\u5efa",
  "risk": "\u98ce\u9669",
  "title": "\u6807\u9898",
  "detail": "\u8be6\u60c5",
  "Detail": "\u8be6\u60c5",
  "Task": "\u4efb\u52a1",
  "Changes": "\u53d8\u66f4",
  "Review": "\u5ba1\u6838",
  "Resource": "\u8d44\u6e90",
  "Capacity": "\u4ea7\u80fd",
  "Assignments": "\u5206\u914d",
  "None": "\u65e0",
  "Name": "\u540d\u79f0",
  "Captured": "\u6355\u83b7\u65f6\u95f4",
  "Items": "\u9879\u76ee",
  "Sync marked follow-ups": "\u540c\u6b65\u5df2\u6807\u8bb0\u7684\u8ddf\u8fdb\u4efb\u52a1",
  "Follow-up": "\u8ddf\u8fdb",
  "Due": "\u5230\u671f",
  "Sync": "\u540c\u6b65",
  "not synced": "\u672a\u540c\u6b65",
  "display name": "\u663e\u793a\u540d\u79f0",
  "Role": "\u89d2\u8272",
  "pending": "\u5f85\u5ba1\u6838",
  "approved": "\u5df2\u6279\u51c6",
  "rejected": "\u5df2\u62d2\u7edd",
  "open": "\u672a\u5173\u95ed",
  "closed": "\u5df2\u5173\u95ed",
  "Undo": "撤销",
  "Undo last change": "撤销上一次更改",
  "Undo complete": "已撤销",
  "Nothing to undo": "没有可撤销的更改",
  "Translated preview": "翻译预览",
  "Clear board filters": "清除看板筛选",
  "Critical tasks only": "仅显示关键任务",
  "Partner-visible tasks only": "仅显示合作伙伴可见任务",
  "Exit board fullscreen": "退出看板全屏",
  "Open board fullscreen": "打开看板全屏",
  "Exit timeline fullscreen": "退出时间线全屏",
  "Open timeline fullscreen": "打开时间线全屏",
  "Clear multi-selection": "清除多选",
  "This task has a predecessor dependency": "此任务有前置依赖",
  "Linked dependencies": "关联依赖",
  "Sent to Task Planner": "已发送到任务计划器",
  "Internal only": "仅内部可见",
  "Drag to reorder or move this card": "拖动以排序或移动此卡片",
  "Drag to change start date": "拖动以更改开始日期",
  "Drag to change finish date": "拖动以更改完成日期",
  "Drag task label to move dates": "拖动任务标签以移动日期",
  "Pan the timeline by dragging empty space. Shift + mouse wheel scrolls sideways.": "拖动空白区域可平移时间线。Shift + 鼠标滚轮可横向滚动。",
  "No phase": "无阶段",
  "No matching owners": "没有匹配的负责人",
  "All": "全部",
  "Original manufacturing contract is complete; the remaining blocker is the outstanding balance.": "原制造合同已完成，剩余阻碍是未付尾款。",
  "Dylan to pay the remaining manufacturing amount.": "Dylan 支付剩余制造款项。",
  "Factory/Grandtop confirmation that the remaining amount has landed.": "工厂/Grandtop 确认尾款已经到账。",
  "Confirm exact model split: external X box vs internal/I variants before the main production plastic run.": "在主要塑料生产前确认准确型号比例：外置 X 外壳与内置/I 版本的分配。",
  "Final copy of assembly instructions after procedure review.": "流程审核后形成最终版装配说明。",
  "Keep certification record visible; no new FCC/CE certification work expected for this run.": "保留认证记录可见；本批次预计不需要新的 FCC/CE 认证工作。",
  "Important release gate for labels and product/package marking.": "标签以及产品/包装标识的重要放行节点。",
  "Firmware sign-off before production QA.": "生产 QA 前完成固件签核。",
  "Validate PestSense QA dashboard flow before production.": "生产前验证 PestSense QA 看板流程。",
  "Grandtop-side hand scanner must be working before production QA.": "生产 QA 前，Grandtop 侧手持扫描器必须可用。",
  "Visit timing depends on commercial readiness, part availability, plastic sample timing, and production readiness.": "访问时间取决于商务准备、零件到货、塑料样品时间以及生产准备情况。",
  "On-site visit covering initial production, QA checks, temperature/vibration review, and factory coordination.": "现场访问包括初始生产、QA 检查、温度/振动复核以及工厂协调。",
  "Confirm the load cell type is correct, who is shipping them, and whether anything has already been dispatched.": "确认称重传感器型号是否正确、由谁发货，以及是否已有物料发出。",
  "1,860 devices require two load cells each: 3,720 load cells total.": "1,860 台设备每台需要两个称重传感器：合计 3,720 个。",
  "Double-check Grandtop already has the membrane stock on hand.": "再次确认 Grandtop 已有薄膜库存。",
  "Order the bare PCB boards; this is one of the main long-lead items.": "订购裸 PCB 板；这是主要长交期项目之一。",
  "PCBA assembly is short, but the assembly line/time has to be booked with Grandtop management.": "PCBA 组装时间较短，但必须与 Grandtop 管理层预约产线/时间。",
  "Assemble PCBAs after boards/components are available and the assembly slot is booked.": "PCB 板和元件到位且组装时段预约后，进行 PCBA 组装。",
  "Double-check whether cables are already available; if not, order/make them with roughly two-week allowance.": "再次确认线缆是否已有库存；如没有，按约两周余量安排订购/制作。",
  "Expected to already be in stock; confirm count and availability.": "预计已有库存；确认数量和可用性。",
  "Confirm the remaining small components are already available.": "确认其余小元件已经可用。",
  "Confirm whether all 1,860 devices use the external box, or a split across X/I variants.": "确认 1,860 台设备是否全部使用外置盒，或在 X/I 版本之间拆分。",
  "Resolve material choice because previous housings had serious warping issues.": "解决材料选择问题，因为之前外壳存在严重翘曲。",
  "Validate that the plastic supplier uses suitable UV protection.": "验证塑料供应商使用合适的 UV 防护。",
  "Confirm whether a thermal additive/material is needed to reduce heat issues.": "确认是否需要导热添加剂/材料以降低发热问题。",
  "Grey is preferred over black for thermal performance unless there is a reason not to change.": "为改善热性能，优先使用灰色而不是黑色，除非有理由不更改。",
  "Samples are mainly needed for the outer box, lid, and tray; other plastics are already proven.": "样品主要需要外盒、盖子和托盘；其他塑料件已验证。",
  "Supplier produces a small injection run to validate material and warping.": "供应商进行小批量注塑，以验证材料和翘曲情况。",
  "Validate samples for warping, quality, fit, and acceptable finish.": "验证样品的翘曲、质量、配合和表面效果是否可接受。",
  "Decision gate before committing to the full plastic production run.": "投入完整塑料生产前的决策节点。",
  "Drop testing is about how the plastics survive impact, so it depends on plastics being available.": "跌落测试用于验证塑料件抗冲击能力，因此依赖塑料件到位。",
  "Gather updated ROHS documentation, certificates, and datasheets for chosen plastic/materials.": "收集所选塑料/材料的最新 ROHS 文件、证书和数据表。",
  "Run additional temperature-related checks while Dylan is on site.": "Dylan 在现场期间进行额外温度相关检查。",
  "Run a pragmatic vibration check during the site visit.": "现场访问期间进行实用的振动检查。",
  "Keep transport testing in scope; remove water-submerge, sand/dust ingress, salt fog, and button-press tests from this run.": "保留运输测试范围；本批次移除水浸、砂尘侵入、盐雾和按键按压测试。",
  "Refresh the mixed Chinese/English assembly procedure and check for small production changes.": "更新中英文混合装配流程，并检查小的生产变更。",
  "Validate that the current QA procedure still works before the run.": "批量生产前验证当前 QA 流程仍然可用。",
  "Investigate Dylan's idea for a new QA software experience, especially around Great Firewall reliability.": "评估 Dylan 关于新 QA 软件体验的想法，特别是防火墙环境下的可靠性。",
  "Confirm the quality plan for assembly, dashboard checks, scanner checks, and acceptance criteria.": "确认装配、看板检查、扫描器检查和验收标准的质量计划。",
  "Initial production run while Dylan is on site.": "Dylan 在现场期间进行初始生产。",
  "Remaining production after the first 200 units. Factory rate is about 200 units/day and Sundays are not working days.": "首批 200 台之后的剩余生产。工厂速度约为每天 200 台，周日不工作。",
  "Review existing packaging designs before using them on a mass run.": "批量使用前审核现有包装设计。",
  "Confirm cardboard material for the main packaging.": "确认主包装纸板材料。",
  "Confirm thick cardboard corner protectors for shipping protection.": "确认用于运输防护的厚纸板护角。",
  "Validate packaging construction and protective performance.": "验证包装结构和防护性能。",
  "Confirm external branding/labeling on the packaging.": "确认包装上的外部品牌/标签。",
  "Order packaging materials after design/material validation.": "设计/材料验证后订购包装材料。",
  "Confirm shipping split, freight forwarder, evidence package, and booking details.": "确认发货拆分、货代、凭证资料包和订舱细节。",
  "Imported MPP dates need Dylan review": "导入的 MPP 日期需要 Dylan 审核",
  "The plan was imported from the Version 1.1 manufacturing plan and shifted as a default template. Dates, owners, quantities, and phase names should be reviewed against the current product/run.": "该计划从 1.1 版制造计划导入，并已调整为默认模板。日期、负责人、数量和阶段名称应根据当前产品/批次进行审核。"
};

const LanguageContext = createContext<Language>("en");

function translateText(value: string | null | undefined, language: Language) {
  if (!value) return "";
  if (language === "en") return value;
  return zhTranslations[value] || value;
}

function useT() {
  const language = useContext(LanguageContext);
  return (value: string | null | undefined) => translateText(value, language);
}

function activeUsers(users: User[]) {
  return users.filter((item) => item.is_active);
}

function defaultLanguageForUser(user: Pick<User, "username"> | null | undefined): Language | null {
  const username = user?.username?.toLowerCase();
  return username === "ashley" || username === "grandtop-1" ? "zh" : null;
}

function parentIdSet(tasks: WorkTask[]) {
  const ids = new Set<number>();
  for (const task of tasks) {
    if (task.parent_id) ids.add(task.parent_id);
  }
  return ids;
}

function visibleTasksByCollapse(tasks: WorkTask[], collapsedTaskIds: Set<number>) {
  if (!collapsedTaskIds.size) return tasks;
  const byId = new Map(tasks.map((task) => [task.id, task]));
  return tasks.filter((task) => {
    let parentId = task.parent_id;
    while (parentId) {
      if (collapsedTaskIds.has(parentId)) return false;
      parentId = byId.get(parentId)?.parent_id || null;
    }
    return true;
  });
}

function loadCollapsedTaskIds() {
  try {
    const parsed = JSON.parse(localStorage.getItem("manufacturing_collapsed_tasks") || "[]");
    return new Set<number>(Array.isArray(parsed) ? parsed.filter((item) => Number.isFinite(Number(item))).map(Number) : []);
  } catch {
    return new Set<number>();
  }
}

function dependencyLabel(dep: Dependency) {
  const lag = dep.lag_days ? ` ${dep.lag_days > 0 ? "+" : ""}${dep.lag_days}d` : "";
  return `${dep.dependency_type}${lag}`;
}

function dependencyPathDependencyIds(dependencies: Dependency[], selectedTaskId: number | null) {
  const ids = new Set<number>();
  if (!selectedTaskId) return ids;
  for (const dep of dependencies) {
    if (dep.predecessor_id === selectedTaskId || dep.successor_id === selectedTaskId) ids.add(dep.id);
  }
  return ids;
}

function dependencyIsViolated(dep: Dependency, predecessor: WorkTask, successor: WorkTask) {
  const predStart = parseDate(predecessor.start_date);
  const predFinish = parseDate(predecessor.finish_date);
  const succStart = parseDate(successor.start_date);
  const succFinish = parseDate(successor.finish_date);
  if (!predStart || !predFinish || !succStart || !succFinish) return false;
  const laggedPredStart = addDays(predStart, dep.lag_days || 0);
  const laggedPredFinish = addDays(predFinish, dep.lag_days || 0);
  switch (dep.dependency_type) {
    case "SS": return succStart < laggedPredStart;
    case "FF": return succFinish < laggedPredFinish;
    case "SF": return succFinish < laggedPredStart;
    case "FS":
    default: return succStart < laggedPredFinish;
  }
}

function translatedPreviewText(value: string | null | undefined, language: Language) {
  if (language !== "zh" || !value) return "";
  const translated = translateText(value, language);
  return translated && translated !== value ? translated : "";
}

function TranslationPreview({ value }: { value: string | null | undefined }) {
  const language = useContext(LanguageContext);
  const tr = useT();
  const translated = translatedPreviewText(value, language);
  if (!translated) return null;
  return <span className="translation-preview"><Languages size={12} /> <b>{tr("Translated preview")}</b>{translated}</span>;
}
const defaultBoardPreferences: BoardPreferences = {
  groupBy: "phase",
  density: "comfortable",
  filters: {
    ownerId: "all",
    status: "all",
    category: "all",
    search: "",
    dueWindow: "all",
    criticalOnly: false,
    partnerVisibleOnly: false
  }
};

function tokenHeader(token: string) {
  return { Authorization: `Bearer ${token}` };
}

function formatStatus(value: string) {
  return value.replaceAll("_", " ");
}

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(a: Date, b: Date) {
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
}

function addDays(a: Date, days: number) {
  const next = new Date(a);
  next.setDate(next.getDate() + days);
  return next;
}

function formatIsoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

function parseDate(value: string | null): Date | null {
  return value ? new Date(`${value}T00:00:00`) : null;
}

function inclusiveDurationDays(start: string | null | undefined, finish: string | null | undefined, fallbackSpan = 0) {
  const startDate = parseDate(start || null);
  const finishDate = parseDate(finish || null);
  if (startDate && finishDate) return Math.max(1, daysBetween(startDate, finishDate) + 1);
  return Math.max(1, Math.round(fallbackSpan) + 1);
}

function storedDurationFromInclusive(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value) - 1);
}

function weekOfMonth(value: Date) {
  return Math.floor((value.getDate() - 1) / 7) + 1;
}

function cleanTaskPatch(form: Partial<WorkTask>) {
  const fields: (keyof WorkTask)[] = [
    "title",
    "description",
    "item_type",
    "category",
    "status",
    "priority",
    "owner_user_id",
    "calendar_id",
    "start_date",
    "finish_date",
    "duration_days",
    "scheduling_mode",
    "percent_complete",
    "partner_visible",
    "send_to_taskplanner",
    "budget_cost",
    "actual_cost"
  ];
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    if (field in form) {
      const value = form[field];
      result[field] = value === "" ? null : value;
    }
  }
  return result;
}

function isBoardGroupBy(value: unknown): value is BoardGroupBy {
  return ["phase", "status", "owner", "category", "priority"].includes(String(value));
}

function isBoardDensity(value: unknown): value is BoardDensity {
  return value === "compact" || value === "comfortable";
}

function isDueWindow(value: unknown): value is BoardDueWindow {
  return ["all", "overdue", "next_7", "next_30", "no_finish"].includes(String(value));
}

function loadBoardPreferences(): BoardPreferences {
  try {
    const raw = localStorage.getItem("manufacturing_board_preferences");
    if (!raw) return defaultBoardPreferences;
    const parsed = JSON.parse(raw) as Partial<BoardPreferences>;
    const parsedFilters = (parsed.filters || {}) as Partial<BoardFilters>;
    return {
      groupBy: isBoardGroupBy(parsed.groupBy) ? parsed.groupBy : defaultBoardPreferences.groupBy,
      density: isBoardDensity(parsed.density) ? parsed.density : defaultBoardPreferences.density,
      filters: {
        ownerId: typeof parsedFilters.ownerId === "string" ? parsedFilters.ownerId : defaultBoardPreferences.filters.ownerId,
        status: typeof parsedFilters.status === "string" ? parsedFilters.status : defaultBoardPreferences.filters.status,
        category: typeof parsedFilters.category === "string" ? parsedFilters.category : defaultBoardPreferences.filters.category,
        search: typeof parsedFilters.search === "string" ? parsedFilters.search : defaultBoardPreferences.filters.search,
        dueWindow: isDueWindow(parsedFilters.dueWindow) ? parsedFilters.dueWindow : defaultBoardPreferences.filters.dueWindow,
        criticalOnly: Boolean(parsedFilters.criticalOnly),
        partnerVisibleOnly: Boolean(parsedFilters.partnerVisibleOnly)
      }
    };
  } catch {
    return defaultBoardPreferences;
  }
}

function updateBoardPreferences(current: BoardPreferences, patch: BoardPreferencesPatch) {
  return {
    ...current,
    ...patch,
    filters: {
      ...current.filters,
      ...(patch.filters || {})
    }
  };
}

function userDisplayName(users: User[], userId: number | null) {
  if (!userId) return "Unassigned";
  return users.find((item) => item.id === userId)?.display_name || `User ${userId}`;
}

function sortBoardTasks(items: WorkTask[]) {
  return [...items].sort((a, b) => a.position - b.position || (a.start_date || "").localeCompare(b.start_date || "") || a.id - b.id);
}

function dueWindowMatches(task: WorkTask, dueWindow: BoardDueWindow) {
  if (dueWindow === "all") return true;
  if (dueWindow === "no_finish") return !task.finish_date;
  if (!task.finish_date) return false;
  const today = isoToday();
  if (dueWindow === "overdue") return task.finish_date < today && task.status !== "done";
  const limit = formatIsoDate(addDays(parseDate(today)!, dueWindow === "next_7" ? 7 : 30));
  return task.finish_date >= today && task.finish_date <= limit;
}

function taskMatchesBoardFilters(task: WorkTask, filters: BoardFilters) {
  if (filters.ownerId === "unassigned" && task.owner_user_id) return false;
  if (filters.ownerId !== "all" && filters.ownerId !== "unassigned" && task.owner_user_id !== Number(filters.ownerId)) return false;
  if (filters.status !== "all" && task.status !== filters.status) return false;
  if (filters.category !== "all" && task.category !== filters.category) return false;
  if (filters.criticalOnly && !task.critical) return false;
  if (filters.partnerVisibleOnly && !task.partner_visible) return false;
  if (!dueWindowMatches(task, filters.dueWindow)) return false;
  const search = filters.search.trim().toLowerCase();
  if (search) {
    const haystack = `${task.code || ""} ${task.title} ${task.description || ""}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  return true;
}

function activeBoardFilterCount(filters: BoardFilters) {
  let count = 0;
  if (filters.ownerId !== "all") count += 1;
  if (filters.status !== "all") count += 1;
  if (filters.category !== "all") count += 1;
  if (filters.search.trim()) count += 1;
  if (filters.dueWindow !== "all") count += 1;
  if (filters.criticalOnly) count += 1;
  if (filters.partnerVisibleOnly) count += 1;
  return count;
}

function nextBoardPosition(bucketTasks: WorkTask[], draggedTaskId: number, beforeTaskId: number | null) {
  const ordered = sortBoardTasks(bucketTasks).filter((task) => task.id !== draggedTaskId);
  const requestedIndex = beforeTaskId ? ordered.findIndex((task) => task.id === beforeTaskId) : ordered.length;
  const index = requestedIndex < 0 ? ordered.length : requestedIndex;
  const previous = ordered[index - 1] || null;
  const next = ordered[index] || null;
  if (!previous && !next) return 10;
  if (!previous && next) return Math.max(0, next.position - 10);
  if (previous && !next) return previous.position + 10;
  const midpoint = Math.floor((previous!.position + next!.position) / 2);
  return midpoint > previous!.position ? midpoint : previous!.position + 1;
}

function buildBoardBuckets(tasks: WorkTask[], users: User[], groupBy: BoardGroupBy, filters: BoardFilters): BoardBucket[] {
  const hasActiveFilters = activeBoardFilterCount(filters) > 0;
  const cardTasks = tasks.filter((task) => task.item_type !== "phase").filter((task) => taskMatchesBoardFilters(task, filters));
  const sortedCards = sortBoardTasks(cardTasks);

  if (groupBy === "phase") {
    const phases = sortBoardTasks(tasks.filter((task) => task.item_type === "phase"));
    const phaseIds = new Set(phases.map((phase) => phase.id));
    const buckets: BoardBucket[] = phases.map((phase) => {
      const items = sortedCards.filter((task) => task.parent_id === phase.id);
      return {
        id: `phase:${phase.id}`,
        title: phase.title,
        subtitle: `${items.length} cards`,
        value: phase.id,
        parentId: phase.id,
        tasks: items
      };
    });
    const loose = sortedCards.filter((task) => !task.parent_id || !phaseIds.has(task.parent_id));
    if (loose.length || !buckets.length) {
      buckets.push({ id: "phase:none", title: "No phase", subtitle: `${loose.length} cards`, value: null, parentId: null, tasks: loose });
    }
    return hasActiveFilters ? buckets.filter((bucket) => bucket.tasks.length > 0) : buckets;
  }

  if (groupBy === "status") {
    return statuses.map((status) => {
      const items = sortedCards.filter((task) => task.status === status);
      return { id: `status:${status}`, title: formatStatus(status), subtitle: `${items.length} cards`, value: status, tasks: items };
    });
  }

  if (groupBy === "category") {
    return categories.map((category) => {
      const items = sortedCards.filter((task) => task.category === category);
      return { id: `category:${category}`, title: category, subtitle: `${items.length} cards`, value: category, tasks: items };
    });
  }

  if (groupBy === "priority") {
    return priorities.map((priority) => {
      const items = sortedCards.filter((task) => task.priority === priority);
      return { id: `priority:${priority}`, title: priority, subtitle: `${items.length} cards`, value: priority, tasks: items };
    });
  }

  const ownerIds = new Set(sortedCards.map((task) => task.owner_user_id || 0));
  const ownerBuckets: BoardBucket[] = users
    .filter((item) => ownerIds.has(item.id))
    .map((owner) => {
      const items = sortedCards.filter((task) => task.owner_user_id === owner.id);
      return { id: `owner:${owner.id}`, title: owner.display_name, subtitle: `${items.length} cards`, value: owner.id, tasks: items };
    });
  if (ownerIds.has(0)) {
    const items = sortedCards.filter((task) => !task.owner_user_id);
    ownerBuckets.unshift({ id: "owner:unassigned", title: "Unassigned", subtitle: `${items.length} cards`, value: null, tasks: items });
  }
  return ownerBuckets.length ? ownerBuckets : [{ id: "owner:none", title: "No matching owners", subtitle: "0 cards", value: null, tasks: [] }];
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("manufacturing_token") || "");
  const [language, setLanguage] = useState<Language>(() => localStorage.getItem("manufacturing_language") === "zh" ? "zh" : "en");
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [selectionAnchorId, setSelectionAnchorId] = useState<number | null>(null);
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<Set<number>>(() => loadCollapsedTaskIds());
  const [dependencyFocus, setDependencyFocus] = useState(false);
  const [dependencyMode, setDependencyMode] = useState<DependencyMode>(() => {
    const saved = localStorage.getItem("manufacturing_dependency_mode");
    return saved === "all" || saved === "off" ? saved : "selected";
  });
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>(() => localStorage.getItem("manufacturing_workspace_view") === "board" ? "board" : "timeline");
  const [viewMode, setViewMode] = useState<"wbs" | "category">("wbs");
  const [zoom, setZoom] = useState<"month" | "quarter" | "half">("quarter");
  const [boardPreferences, setBoardPreferences] = useState<BoardPreferences>(() => loadBoardPreferences());
  const [tab, setTab] = useState<Tab>("risks");
  const [leftWidth, setLeftWidth] = useState(330);
  const [inspectorWidth, setInspectorWidth] = useState(360);
  const [bottomHeight, setBottomHeight] = useState(260);
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const [timelineFullScreen, setTimelineFullScreen] = useState(false);
  const [boardFullScreen, setBoardFullScreen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourPromptOpen, setTourPromptOpen] = useState(false);
  const [tourPromptSeen, setTourPromptSeen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [undoAction, setUndoAction] = useState<UndoAction | null>(null);
  const [busy, setBusy] = useState(false);
  const taskTreeScrollRef = useRef<HTMLDivElement | null>(null);
  const ganttScrollRef = useRef<HTMLDivElement | null>(null);
  const verticalScrollSyncRef = useRef(false);

  const canEdit = user?.role === "admin" || user?.role === "editor";
  const workspaceFullScreen = timelineFullScreen || boardFullScreen;
  const tr = (value: string | null | undefined) => translateText(value, language);
  const orderedTaskIds = useMemo(() => workspace?.tasks.map((task) => task.id) || [], [workspace]);
  const workspaceUsers = useMemo(() => activeUsers(workspace?.users || []), [workspace?.users]);
  const visibleTimelineTasks = useMemo(() => visibleTasksByCollapse(workspace?.tasks || [], collapsedTaskIds), [workspace?.tasks, collapsedTaskIds]);

  async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(path, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? tokenHeader(token) : {}),
        ...(options.headers || {})
      }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || response.statusText);
    }
    return response.json();
  }

  async function loadMe(currentToken = token) {
    if (!currentToken) return;
    const response = await fetch("/api/auth/me", { headers: tokenHeader(currentToken) });
    if (!response.ok) {
      localStorage.removeItem("manufacturing_token");
      setToken("");
      setUser(null);
      return;
    }
    const currentUser = await response.json();
    setUser(currentUser);
    const defaultLanguage = defaultLanguageForUser(currentUser);
    if (defaultLanguage) setLanguage(defaultLanguage);
  }

  async function loadProjects() {
    const data = await api<Project[]>("/api/projects");
    setProjects(data);
    if (!projectId && data.length) setProjectId(data[0].id);
  }

  async function loadWorkspace(id = projectId) {
    if (!id) return;
    const data = await api<Workspace>(`/api/projects/${id}/workspace`);
    setWorkspace(data);
    if (!selectedTaskId && data.tasks.length) setSelectedTaskId(data.tasks[0].id);
  }

  useEffect(() => {
    if (token) loadMe(token).catch((error) => setMessage(error.message));
  }, []);

  useEffect(() => {
    if (user) loadProjects().catch((error) => setMessage(error.message));
  }, [user]);

  useEffect(() => {
    if (projectId) loadWorkspace(projectId).catch((error) => setMessage(error.message));
  }, [projectId]);

  useEffect(() => {
    localStorage.setItem("manufacturing_language", language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem("manufacturing_workspace_view", workspaceView);
  }, [workspaceView]);

  useEffect(() => {
    localStorage.setItem("manufacturing_collapsed_tasks", JSON.stringify([...collapsedTaskIds]));
  }, [collapsedTaskIds]);

  useEffect(() => {
    localStorage.setItem("manufacturing_dependency_mode", dependencyMode);
  }, [dependencyMode]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (event.key.toLowerCase() === "h") {
        event.preventDefault();
        setDependencyFocus((value) => !value);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    localStorage.setItem("manufacturing_board_preferences", JSON.stringify(boardPreferences));
  }, [boardPreferences]);

  useEffect(() => {
    if (!user || !workspace || tourPromptSeen) return;
    if (localStorage.getItem(helpTourStorageKey) === "1") return;
    setTourPromptSeen(true);
    setTourPromptOpen(true);
  }, [user, workspace, tourPromptSeen]);

  useEffect(() => {
    if (!workspace) {
      setSelectedTaskIds([]);
      setSelectionAnchorId(null);
      return;
    }
    const taskIds = new Set(workspace.tasks.map((task) => task.id));
    setSelectedTaskIds((current) => {
      const filtered = current.filter((id) => taskIds.has(id));
      if (filtered.length) return filtered;
      if (selectedTaskId && taskIds.has(selectedTaskId)) return [selectedTaskId];
      return workspace.tasks[0] ? [workspace.tasks[0].id] : [];
    });
    if (selectedTaskId && !taskIds.has(selectedTaskId)) {
      setSelectedTaskId(workspace.tasks[0]?.id || null);
    }
  }, [workspace, selectedTaskId]);

  const selectedTask = workspace?.tasks.find((task) => task.id === selectedTaskId) || workspace?.tasks[0] || null;

  function selectTask(taskId: number, options: TaskSelectOptions = {}) {
    setSelectedTaskId(taskId);
    if (options.preserveSelection) return;
    setSelectedTaskIds((current) => {
      if (options.range && selectionAnchorId && orderedTaskIds.includes(selectionAnchorId)) {
        const anchorIndex = orderedTaskIds.indexOf(selectionAnchorId);
        const targetIndex = orderedTaskIds.indexOf(taskId);
        if (targetIndex >= 0) {
          const startIndex = Math.min(anchorIndex, targetIndex);
          const endIndex = Math.max(anchorIndex, targetIndex);
          return orderedTaskIds.slice(startIndex, endIndex + 1);
        }
      }
      if (options.toggle) {
        const next = current.includes(taskId) ? current.filter((id) => id !== taskId) : [...current, taskId];
        return next.length ? next : [taskId];
      }
      return [taskId];
    });
    if (!options.range) setSelectionAnchorId(taskId);
  }

  function clearMultiSelection() {
    setSelectedTaskIds(selectedTaskId ? [selectedTaskId] : []);
    setSelectionAnchorId(selectedTaskId);
  }

  function toggleCollapsedTask(taskId: number) {
    setCollapsedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function setOutlineLevel(level: "all" | 1 | 2) {
    const tasks = workspace?.tasks || [];
    if (level === "all") {
      setCollapsedTaskIds(new Set());
      return;
    }
    const parentIds = parentIdSet(tasks);
    const next = new Set<number>();
    for (const task of tasks) {
      if (!parentIds.has(task.id)) continue;
      if (depthFor(task, tasks) >= level - 1) next.add(task.id);
    }
    setCollapsedTaskIds(next);
  }

  function selectDependency(dep: Dependency, predecessorId: number, successorId: number, label: string) {
    setSelectedTaskId(successorId);
    setSelectedTaskIds(Array.from(new Set([predecessorId, successorId])));
    setSelectionAnchorId(predecessorId);
    setMessage(`${tr("Dependency selected")}: ${label} (${dependencyLabel(dep)})`);
  }

  async function login(username: string, password: string) {
    setBusy(true);
    try {
      const result = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      if (!result.ok) {
        const body = await result.json().catch(() => ({}));
        const detail = String(body.detail || "");
        if (result.status === 401 && detail.toLowerCase().includes("username or password")) {
          throw new Error("Incorrect username or password");
        }
        throw new Error(detail || "Login failed");
      }
      const data = await result.json();
      localStorage.setItem("manufacturing_token", data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      const defaultLanguage = defaultLanguageForUser(data.user);
      if (defaultLanguage) setLanguage(defaultLanguage);
      setMessage("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  function logout() {
    localStorage.removeItem("manufacturing_token");
    setToken("");
    setUser(null);
    setWorkspace(null);
  }

  function startResize(kind: "left" | "right" | "bottom", event: React.MouseEvent) {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = leftWidth;
    const startRight = inspectorWidth;
    const startBottom = bottomHeight;
    const move = (moveEvent: MouseEvent) => {
      if (kind === "left") setLeftWidth(Math.max(220, Math.min(620, startLeft + moveEvent.clientX - startX)));
      if (kind === "right") setInspectorWidth(Math.max(280, Math.min(620, startRight - (moveEvent.clientX - startX))));
      if (kind === "bottom") setBottomHeight(Math.max(140, Math.min(520, startBottom - (moveEvent.clientY - startY))));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  function resetTimelineView() {
    const gantt = ganttScrollRef.current;
    const tree = taskTreeScrollRef.current;
    if (gantt) {
      gantt.scrollLeft = 0;
      gantt.scrollTop = 0;
      gantt.dispatchEvent(new Event("scroll", { bubbles: true }));
    }
    if (tree) {
      tree.scrollTop = 0;
      tree.dispatchEvent(new Event("scroll", { bubbles: true }));
    }
    setMessage("Timeline reset to project start");
  }

  function startHelpTour() {
    setWorkspaceView("timeline");
    setTourPromptOpen(false);
    setTourStepIndex(0);
  }

  function dismissHelpTour(remember: boolean) {
    if (remember) localStorage.setItem(helpTourStorageKey, "1");
    setTourPromptOpen(false);
    setTourStepIndex(null);
  }

  function finishHelpTour() {
    localStorage.setItem(helpTourStorageKey, "1");
    setTourStepIndex(null);
  }

  function toggleTimelineFullScreen() {
    const next = !timelineFullScreen;
    setTimelineFullScreen(next);
    setBoardFullScreen(false);
    if (next) {
      setBottomCollapsed(true);
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
    }
  }

  function toggleBoardFullScreen() {
    const next = !boardFullScreen;
    setBoardFullScreen(next);
    setTimelineFullScreen(false);
    if (next) {
      setBottomCollapsed(true);
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
    }
  }

  function changeWorkspaceView(value: WorkspaceView) {
    setWorkspaceView(value);
    setTimelineFullScreen(false);
    setBoardFullScreen(false);
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
  }

  function syncVerticalScroll(source: "tree" | "gantt") {
    const sourceElement = source === "tree" ? taskTreeScrollRef.current : ganttScrollRef.current;
    const targetElement = source === "tree" ? ganttScrollRef.current : taskTreeScrollRef.current;
    if (!sourceElement || !targetElement || verticalScrollSyncRef.current) return;
    const nextTop = sourceElement.scrollTop;
    if (Math.abs(targetElement.scrollTop - nextTop) < 1) return;
    verticalScrollSyncRef.current = true;
    targetElement.scrollTop = nextTop;
    window.requestAnimationFrame(() => {
      verticalScrollSyncRef.current = false;
    });
  }

  async function runAction(action: () => Promise<unknown>, success: string, undo?: UndoAction | null) {
    setBusy(true);
    try {
      await action();
      if (undo !== undefined) setUndoAction(undo);
      setMessage(success);
      await loadWorkspace();
      await loadProjects();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  function taskPatchUndo(taskId: number, patch: Record<string, unknown>, label = "task") {
    const task = workspace?.tasks.find((item) => item.id === taskId);
    if (!task) return null;
    const before: Record<string, unknown> = {};
    for (const key of Object.keys(patch)) {
      before[key] = task[key as keyof WorkTask] ?? null;
    }
    return { label, run: () => api(`/api/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify(before) }) };
  }

  function dateChangesUndo(changes: TaskDateChange[]) {
    const before = changes
      .map((change) => {
        const task = workspace?.tasks.find((item) => item.id === change.taskId);
        if (!task) return null;
        return {
          taskId: change.taskId,
          body: {
            start_date: task.start_date,
            finish_date: task.finish_date,
            duration_days: task.duration_days,
            scheduling_mode: task.scheduling_mode
          }
        };
      })
      .filter(Boolean) as TaskDateChange[];
    if (!before.length) return null;
    return {
      label: `${before.length} date change${before.length === 1 ? "" : "s"}`,
      run: async () => {
        for (const change of before) {
          await api(`/api/tasks/${change.taskId}`, { method: "PATCH", body: JSON.stringify(change.body) });
        }
      }
    };
  }

  async function undoLastAction() {
    if (!undoAction) {
      setMessage("Nothing to undo");
      return;
    }
    const action = undoAction;
    setUndoAction(null);
    await runAction(action.run, "Undo complete", null);
  }

  if (!user) return <LanguageContext.Provider value={language}><Login busy={busy} onLogin={login} message={message} language={language} onLanguageChange={setLanguage} /></LanguageContext.Provider>;

  const messageRow = message ? "34px " : "";
  const gridTemplateRows = workspaceFullScreen
    ? `58px ${messageRow}minmax(0, 1fr)`
    : bottomCollapsed
      ? `58px ${messageRow}minmax(0, 1fr) 42px`
      : `58px ${messageRow}minmax(0, 1fr) ${bottomHeight}px`;

  const mainGridTemplateColumns = boardFullScreen
    ? "1fr"
    : timelineFullScreen
      ? `${leftWidth}px 6px minmax(420px, 1fr)`
      : `${leftWidth}px 6px minmax(420px, 1fr) 6px ${inspectorWidth}px`;

  return (
    <LanguageContext.Provider value={language}>
    <div
      className="app-shell"
      style={{ gridTemplateRows }}
    >
      <header className="topbar">
        <div className="brand-block">
          <div className="brand-title">{tr("Manufacturing Projects")}</div>
          <div className="brand-subtitle">{user.display_name} · {formatStatus(user.role)}</div>
        </div>
        <select value={projectId || ""} onChange={(event) => setProjectId(Number(event.target.value))}>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>{tr(project.name)}</option>
          ))}
        </select>
        <div className="tour-target-inline" data-tour="view-switch">
          <Segmented
            value={workspaceView}
            options={[
              ["timeline", <><GitBranch size={15} /> {tr("Timeline")}</>],
              ["board", <><Kanban size={15} /> {tr("Board")}</>]
            ]}
            onChange={(value) => changeWorkspaceView(value as WorkspaceView)}
          />
        </div>
        <div className="top-metrics">
          <span><ClipboardCheck size={15} />{tr(formatStatus(workspace?.project.status || "planning"))}</span>
          <span><AlertTriangle size={15} />{projects.find((item) => item.id === projectId)?.critical_count || 0} {tr("critical")}</span>
          <span><CalendarDays size={15} />{workspace?.project.start_date}</span>
        </div>
        <button data-tour="recalculate" title={tr("Recalculate schedule")} disabled={!canEdit || busy || !projectId} onClick={() => runAction(() => api(`/api/projects/${projectId}/schedule/recalculate`, { method: "POST" }), "Schedule recalculated", undefined)}>
          <RefreshCw size={17} /> {tr("Recalc")}
        </button>
        <button title={undoAction ? `${tr("Undo last change")}: ${undoAction.label}` : tr("Nothing to undo")} disabled={!undoAction || busy} onClick={undoLastAction}>
          <Undo2 size={17} /> {tr("Undo")}
        </button>
        <LanguageSelect language={language} onChange={setLanguage} />
        <button data-tour="help" title={tr("Show timeline help")} onClick={() => setHelpOpen(true)}><HelpCircle size={17} /></button>
        <button title={tr("Sign out")} onClick={logout}><LogOut size={17} /></button>
      </header>

      {message && <div className="message-line"><span>{tr(message)}</span><button className="message-close" title={tr("Dismiss notification")} onClick={() => setMessage("")}><XCircle size={16} /></button></div>}

      <main
        className={`workspace-grid ${workspaceFullScreen ? "timeline-expanded" : ""}`}
        style={{ gridTemplateColumns: mainGridTemplateColumns }}
      >
        {!boardFullScreen && (
          <>
            <section className="tree-pane">
              <div className="pane-toolbar tree-toolbar">
                <Segmented value={viewMode} options={[["wbs", <ListTree size={16} />], ["category", <Rows3 size={16} />]]} onChange={(value) => setViewMode(value as "wbs" | "category")} />
                {viewMode === "wbs" && <div className="outline-tools">
                  <button title={tr("Collapse to phases")} onClick={() => setOutlineLevel(1)}>L1</button>
                  <button title={tr("Show work packages")} onClick={() => setOutlineLevel(2)}>L2</button>
                  <button title={tr("Show all tasks")} onClick={() => setOutlineLevel("all")}>{tr("All")}</button>
                </div>}
              </div>
              <TaskTree
                tasks={workspace?.tasks || []}
                mode={viewMode}
                selectedTaskId={selectedTask?.id || null}
                selectedTaskIds={selectedTaskIds}
                alignTimeline={workspaceView === "timeline"}
                scrollRef={taskTreeScrollRef}
                onScroll={() => syncVerticalScroll("tree")}
                collapsedTaskIds={collapsedTaskIds}
                onToggleCollapse={toggleCollapsedTask}
                onSelect={selectTask}
              />
            </section>
            <div className="splitter vertical" title={tr("Drag to resize task list")} onMouseDown={(event) => startResize("left", event)}><GripVertical size={14} /></div>
          </>
        )}

        {workspaceView === "timeline" ? (
          <section className="timeline-pane">
            <div className="pane-toolbar">
              <div className="toolbar-title"><GitBranch size={17} /> {tr("Timeline")}</div>
              <div className="timeline-tools">
                <CategoryLegend />
                {selectedTaskIds.length > 1 && <div className="selection-tools"><span>{selectedTaskIds.length} {tr("selected")}</span><button title={tr("Clear multi-selection")} onClick={clearMultiSelection}>{tr("Clear")}</button></div>}
                <label className="toolbar-field compact-field" title={tr("Linked dependencies")}>
                  <Link size={14} />
                  <span>{tr("Links")}</span>
                  <select value={dependencyMode} onChange={(event) => setDependencyMode(event.target.value as DependencyMode)}>
                    <option value="selected">{tr("Selected")}</option>
                    <option value="all">{tr("All")}</option>
                    <option value="off">{tr("Off")}</option>
                  </select>
                </label>
                <button className={dependencyFocus ? "tool-toggle active" : "tool-toggle"} title={tr("Highlight selected dependency path (H)")} onClick={() => setDependencyFocus((value) => !value)}><Link size={15} /> {tr("Path")}</button>
                <button data-tour="timeline-home" title={tr("Return timeline to project start")} onClick={resetTimelineView}><Home size={16} /></button>
                <Segmented value={zoom} options={[["month", "30d"], ["quarter", "90d"], ["half", "180d"]]} onChange={(value) => setZoom(value as "month" | "quarter" | "half")} />
                <button data-tour="timeline-fullscreen" title={tr(timelineFullScreen ? "Exit timeline fullscreen" : "Open timeline fullscreen")} onClick={toggleTimelineFullScreen}>
                  {timelineFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              </div>
            </div>
            <Gantt
              tasks={workspace?.tasks || []}
              visibleTasks={visibleTimelineTasks}
              dependencies={workspace?.dependencies || []}
              mode={viewMode}
              zoom={zoom}
              selectedTaskId={selectedTask?.id || null}
              selectedTaskIds={selectedTaskIds}
              onSelect={selectTask}
              onDependencySelect={selectDependency}
              dependencyFocus={dependencyFocus}
              dependencyMode={dependencyMode}
              canEdit={canEdit}
              externalScrollRef={ganttScrollRef}
              onVerticalScroll={() => syncVerticalScroll("gantt")}
              onTaskDateChange={(changes) => runAction(async () => {
                for (const change of changes) {
                  await api(`/api/tasks/${change.taskId}`, { method: "PATCH", body: JSON.stringify(change.body) });
                }
              }, changes.length > 1 ? `${changes.length} task dates updated` : "Task dates updated", dateChangesUndo(changes))}
            />
          </section>
        ) : (
          <BoardView
            tasks={workspace?.tasks || []}
            users={workspaceUsers}
            dependencies={workspace?.dependencies || []}
            preferences={boardPreferences}
            selectedTaskId={selectedTask?.id || null}
            canEdit={canEdit}
            fullScreen={boardFullScreen}
            onSelect={(id) => selectTask(id)}
            onNotice={setMessage}
            onPreferencesChange={(patch) => setBoardPreferences((current) => updateBoardPreferences(current, patch))}
            onToggleFullScreen={toggleBoardFullScreen}
            onMove={(taskId, body) => runAction(() => api(`/api/tasks/${taskId}/reorder`, { method: "PATCH", body: JSON.stringify(body) }), "Board updated", taskPatchUndo(taskId, body as Record<string, unknown>, "board move"))}
          />
        )}

        {!workspaceFullScreen && (
          <>
            <div className="splitter vertical" title={tr("Drag to resize task inspector")} onMouseDown={(event) => startResize("right", event)}><GripVertical size={14} /></div>
            <TaskInspector
              task={selectedTask}
              users={workspaceUsers}
              calendars={workspace?.calendars || []}
              canEdit={canEdit}
              role={user.role}
              busy={busy}
              onSave={(taskId, body) => { const patch = cleanTaskPatch(body); return runAction(() => api(`/api/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify(patch) }), "Task saved", taskPatchUndo(taskId, patch, "task edit")); }}
              onPropose={(body) => runAction(() => api("/api/change-proposals", { method: "POST", body: JSON.stringify(body) }), "Proposal submitted")}
            />
          </>
        )}
      </main>

      {!workspaceFullScreen && <section className="ops-pane">
        <div className="bottom-resizer" title={tr("Drag to resize lower panel")} onMouseDown={(event) => startResize("bottom", event)} />
        <div className="tabs">
          {(["risks", "decisions", "proposals", "resources", "baselines", "taskplanner", "people"] as Tab[]).map((item) => (
            <button key={item} className={tab === item ? "active" : ""} onClick={() => setTab(item)}>{tr(item)}</button>
          ))}
          <button className="collapse-tab" aria-label={tr(bottomCollapsed ? "Expand lower panel" : "Collapse lower panel")} title={tr(bottomCollapsed ? "Expand lower panel" : "Collapse lower panel")} onClick={() => setBottomCollapsed((value) => !value)}>
            {bottomCollapsed ? <Maximize2 size={15} /> : <Minimize2 size={15} />}
            <span>{tr(bottomCollapsed ? "Expand lower panel" : "Collapse lower panel")}</span>
          </button>
        </div>
        {!bottomCollapsed && workspace && (
          <OpsTab
            tab={tab}
            workspace={workspace}
            canEdit={canEdit}
            isAdmin={user.role === "admin"}
            busy={busy}
            onAction={runAction}
            api={api}
            projectId={projectId}
          />
        )}
      </section>}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
      {tourPromptOpen && <TourPrompt onStart={startHelpTour} onLater={() => dismissHelpTour(false)} onDismiss={() => dismissHelpTour(true)} />}
      {tourStepIndex !== null && <GuidedTour stepIndex={tourStepIndex} steps={tourSteps} onStepChange={setTourStepIndex} onClose={() => dismissHelpTour(false)} onFinish={finishHelpTour} />}
    </div>
    </LanguageContext.Provider>
  );
}

function Login({ busy, onLogin, message, language, onLanguageChange }: { busy: boolean; onLogin: (username: string, password: string) => void; message: string; language: Language; onLanguageChange: (language: Language) => void }) {
  const [username, setUsername] = useState("dylan");
  const [password, setPassword] = useState("");
  const tr = useT();
  return (
    <main className="login-screen">
      <div className="login-language"><LanguageSelect language={language} onChange={onLanguageChange} /></div>
      <form className="login-panel" onSubmit={(event) => { event.preventDefault(); onLogin(username, password); }}>
        <div>
          <h1>{tr("Manufacturing Projects")}</h1>
          <p>{tr("Internal schedule and operations workspace")}</p>
        </div>
        <label>{tr("Username")}<input value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <label>{tr("Password")}<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
        <button disabled={busy} type="submit"><ShieldCheck size={17} /> {tr("Sign in")}</button>
        {message && <div className="form-error">{tr(message)}</div>}
      </form>
    </main>
  );
}

function LanguageSelect({ language, onChange }: { language: Language; onChange: (language: Language) => void }) {
  const tr = useT();
  return (
    <label className="language-select" title={tr("Language")}>
      <Languages size={15} />
      <select aria-label={tr("Language")} value={language} onChange={(event) => onChange(event.target.value as Language)}>
        <option value="en">English</option>
        <option value="zh">{"\u4e2d\u6587"}</option>
      </select>
    </label>
  );
}

function Segmented({ value, options, onChange }: { value: string; options: [string, React.ReactNode][]; onChange: (value: string) => void }) {
  return (
    <div className="segmented">
      {options.map(([key, label]) => (
        <button key={key} className={value === key ? "active" : ""} onClick={() => onChange(key)}>{label}</button>
      ))}
    </div>
  );
}

function CategoryLegend() {
  const tr = useT();
  return (
    <div className="category-legend" title={tr("Task colours follow manufacturing category")}>
      {categories.slice(0, 6).map((category) => (
        <span key={category}><i className={`cat-swatch cat-${category}`} />{tr(category)}</span>
      ))}
    </div>
  );
}

function HelpModal({ onClose }: { onClose: () => void }) {
  const tr = useT();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section className="help-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <h2>{tr("Timeline Help")}</h2>
          <button title={tr("Close help")} onClick={onClose}><XCircle size={17} /></button>
        </div>
        <div className="help-grid expanded-help">
          <div className="help-section">
            <h3>{tr("Navigation")}</h3>
            <p>{tr("The Home button resets horizontal and vertical timeline scroll to the project start.")}</p>
            <p>{tr("Timeline fullscreen keeps the task list visible so meeting views still have row context.")}</p>
            <p>{tr("Collapse the lower panel when risks, decisions, or resources are not needed.")}</p>
          </div>
          <div className="help-section">
            <h3>{tr("Timeline controls")}</h3>
            <p><span className="help-bar phase" />{tr("Phase roll-up bars summarize child tasks.")}</p>
            <p><span className="help-bar task" />{tr("Task bars are colour-coded by category.")}</p>
            <p><span className="help-bar critical" />{tr("Red bars are on the current critical path.")}</p>
            <p><span className="help-diamond" />{tr("Diamonds are milestones.")}</p>
          </div>
          <div className="help-section">
            <h3>{tr("Dependencies and dates")}</h3>
            <p><Link size={15} /> {tr("Chain icons mark dependency-linked tasks.")}</p>
            <p>{tr("Dependency lines show task links; press H to highlight the selected task path.")}</p>
            <p>{tr("Drag a task bar to move dates; drag its left or right edge to resize.")}</p>
            <p>{tr("Use the task inspector's weekend checkbox to switch between elapsed days and workdays.")}</p>
          </div>
          <div className="help-section">
            <h3>{tr("Board")}</h3>
            <p>{tr("Board view shows the same project tasks as cards; use grouping and search to scan ownership or blockers.")}</p>
            <p>{tr("Recalculate schedule after dependency, duration, or schedule edits.")}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function TourPrompt({ onStart, onLater, onDismiss }: { onStart: () => void; onLater: () => void; onDismiss: () => void }) {
  const tr = useT();
  return (
    <div className="modal-backdrop" onClick={onLater}>
      <section className="tour-card prompt-card" onClick={(event) => event.stopPropagation()}>
        <h2>{tr("Quick tour")}</h2>
        <p>{tr("Would you like a quick tour?")}</p>
        <p>{tr("The tour highlights the main controls Dylan mentioned: fullscreen, Home, Timeline/Board, Recalc, and Help.")}</p>
        <div className="tour-actions">
          <button onClick={onStart}><HelpCircle size={16} /> {tr("Start tour")}</button>
          <button onClick={onLater}>{tr("Not now")}</button>
          <button onClick={onDismiss}>{tr("Do not show again")}</button>
        </div>
      </section>
    </div>
  );
}

function GuidedTour({ stepIndex, steps, onStepChange, onClose, onFinish }: { stepIndex: number; steps: TourStep[]; onStepChange: (index: number) => void; onClose: () => void; onFinish: () => void }) {
  const tr = useT();
  const step = steps[stepIndex];
  useEffect(() => {
    const target = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
    target?.classList.add("tour-highlight");
    target?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    return () => target?.classList.remove("tour-highlight");
  }, [step.target]);
  const last = stepIndex === steps.length - 1;
  return (
    <div className="tour-overlay">
      <section className="tour-card floating-tour">
        <div className="tour-progress">{tr("Step")} {stepIndex + 1}/{steps.length}</div>
        <h2>{tr(step.title)}</h2>
        <p>{tr(step.body)}</p>
        <div className="tour-actions">
          <button disabled={stepIndex === 0} onClick={() => onStepChange(stepIndex - 1)}>{tr("Back")}</button>
          {last ? <button onClick={onFinish}>{tr("Finish")}</button> : <button onClick={() => onStepChange(stepIndex + 1)}>{tr("Next")}</button>}
          <button onClick={onClose}>{tr("Skip")}</button>
        </div>
      </section>
    </div>
  );
}

function TaskTree({ tasks, mode, selectedTaskId, selectedTaskIds, collapsedTaskIds, alignTimeline, scrollRef, onScroll, onToggleCollapse, onSelect }: {
  tasks: WorkTask[];
  mode: "wbs" | "category";
  selectedTaskId: number | null;
  selectedTaskIds: number[];
  collapsedTaskIds: Set<number>;
  alignTimeline: boolean;
  scrollRef: { current: HTMLDivElement | null };
  onScroll: () => void;
  onToggleCollapse: (id: number) => void;
  onSelect: (id: number, options?: TaskSelectOptions) => void;
}) {
  const tr = useT();
  const parentIds = useMemo(() => parentIdSet(tasks), [tasks]);
  const rows = useMemo(() => {
    if (mode === "category") {
      const grouped: { header: string; tasks: WorkTask[] }[] = [];
      for (const category of categories) {
        const items = tasks.filter((task) => task.category === category && task.item_type !== "phase");
        if (items.length) grouped.push({ header: category, tasks: items });
      }
      return grouped;
    }
    return [{ header: "WBS", tasks: visibleTasksByCollapse(tasks, collapsedTaskIds) }];
  }, [tasks, mode, collapsedTaskIds]);
  return (
    <div className={`task-tree ${alignTimeline ? "timeline-aligned" : ""}`} ref={scrollRef} onScroll={onScroll}>
      {alignTimeline && <div className="tree-timeline-header-spacer">{tr("Tasks")}</div>}
      {rows.map((group) => (
        <div key={group.header}>
          {mode === "category" && <div className="tree-group">{tr(group.header)}</div>}
          {group.tasks.map((task, taskIndex) => {
            const hasChildren = parentIds.has(task.id) && mode === "wbs";
            const collapsed = collapsedTaskIds.has(task.id);
            return (
              <button
                key={task.id}
                data-task-id={task.id}
                className={`tree-row ${taskIndex % 2 === 0 ? "row-even" : "row-odd"} ${selectedTaskIds.includes(task.id) ? "selected" : ""} ${selectedTaskId === task.id ? "primary-selected" : ""} ${task.item_type}`}
                style={{ paddingLeft: mode === "wbs" ? `${8 + depthFor(task, tasks) * 16}px` : undefined }}
                onClick={(event) => onSelect(task.id, { toggle: event.ctrlKey || event.metaKey, range: event.shiftKey })}
              >
                <span
                  className={hasChildren ? "tree-collapse" : "tree-collapse empty"}
                  title={hasChildren ? tr(collapsed ? "Expand this branch" : "Collapse this branch") : undefined}
                  onClick={(event) => {
                    if (!hasChildren) return;
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleCollapse(task.id);
                  }}
                >
                  {hasChildren ? (collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />) : null}
                </span>
                <span className={`status-dot ${task.status}`} />
                <span className="task-code">{task.code}</span>
                <span className="tree-title">{tr(task.title)}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function depthFor(task: WorkTask, tasks: WorkTask[]) {
  let depth = 0;
  let parent = tasks.find((item) => item.id === task.parent_id) || null;
  while (parent) {
    depth += 1;
    parent = tasks.find((item) => item.id === parent?.parent_id) || null;
  }
  return depth;
}

function BoardView({ tasks, users, dependencies, preferences, selectedTaskId, canEdit, fullScreen, onSelect, onNotice, onPreferencesChange, onToggleFullScreen, onMove }: {
  tasks: WorkTask[];
  users: User[];
  dependencies: Dependency[];
  preferences: BoardPreferences;
  selectedTaskId: number | null;
  canEdit: boolean;
  fullScreen: boolean;
  onSelect: (id: number) => void;
  onNotice: (message: string) => void;
  onPreferencesChange: (patch: BoardPreferencesPatch) => void;
  onToggleFullScreen: () => void;
  onMove: (taskId: number, body: BoardMovePayload) => Promise<unknown>;
}) {
  const tr = useT();
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);
  const buckets = useMemo(
    () => buildBoardBuckets(tasks, users, preferences.groupBy, preferences.filters),
    [tasks, users, preferences.groupBy, preferences.filters]
  );
  const cardCount = tasks.filter((task) => task.item_type !== "phase").length;
  const visibleCount = buckets.reduce((total, bucket) => total + bucket.tasks.length, 0);
  const dependencyCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const dep of dependencies) {
      counts.set(dep.predecessor_id, (counts.get(dep.predecessor_id) || 0) + 1);
      counts.set(dep.successor_id, (counts.get(dep.successor_id) || 0) + 1);
    }
    return counts;
  }, [dependencies]);

  async function handleDrop(bucket: BoardBucket, beforeTaskId: number | null, event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const taskId = Number(event.dataTransfer.getData("text/plain") || draggedTaskId);
    const task = tasks.find((item) => item.id === taskId);
    setDraggedTaskId(null);
    if (!canEdit || !task || task.item_type === "phase") return;

    const payload: BoardMovePayload = {
      position: nextBoardPosition(bucket.tasks, task.id, beforeTaskId)
    };

    if (preferences.groupBy === "phase") {
      const nextParentId = bucket.parentId ?? null;
      if (nextParentId !== task.parent_id) {
        const confirmed = window.confirm(`Move "${task.title}" into "${bucket.title}"? This changes the WBS phase.`);
        if (!confirmed) return;
        payload.parent_id = nextParentId;
        payload.confirm_parent_change = true;
      }
    } else if (preferences.groupBy === "status") {
      const nextStatus = String(bucket.value || "not_started");
      if (nextStatus !== task.status) payload.status = nextStatus;
    } else {
      const currentValue = preferences.groupBy === "owner"
        ? (task.owner_user_id || null)
        : preferences.groupBy === "category"
          ? task.category
          : task.priority;
      if (bucket.value !== currentValue) {
        onNotice("Owner, category, and priority boards are scan views for now. Reorder within a bucket or edit the card in the inspector.");
        return;
      }
    }

    onSelect(task.id);
    await onMove(task.id, payload);
  }

  return (
    <section className="board-pane">
      <BoardToolbar
        users={users}
        preferences={preferences}
        fullScreen={fullScreen}
        cardCount={cardCount}
        visibleCount={visibleCount}
        onPreferencesChange={onPreferencesChange}
        onToggleFullScreen={onToggleFullScreen}
      />
      <div className={`board-scroll board-${preferences.density}`}>
        <div className="board-columns">
          {buckets.map((bucket) => (
            <section
              key={bucket.id}
              className="board-bucket"
              onDragOver={(event) => { if (canEdit) event.preventDefault(); }}
              onDrop={(event) => handleDrop(bucket, null, event)}
            >
              <div className="board-bucket-head">
                <div>
                  <h3>{tr(bucket.title)}</h3>
                  <span>{bucket.tasks.length} {tr("cards")}</span>
                </div>
                <Columns3 size={17} />
              </div>
              <div className="board-cards">
                {bucket.tasks.map((task) => (
                  <BoardCard
                    key={task.id}
                    task={task}
                    ownerName={userDisplayName(users, task.owner_user_id)}
                    dependencyCount={dependencyCounts.get(task.id) || 0}
                    selected={selectedTaskId === task.id}
                    density={preferences.density}
                    canEdit={canEdit}
                    dragging={draggedTaskId === task.id}
                    onSelect={() => onSelect(task.id)}
                    onDragStart={(event) => {
                      setDraggedTaskId(task.id);
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", String(task.id));
                    }}
                    onDropBefore={(event) => handleDrop(bucket, task.id, event)}
                    onDragEnd={() => setDraggedTaskId(null)}
                  />
                ))}
                {!bucket.tasks.length && <div className="board-empty">{tr("No cards")}</div>}
              </div>
            </section>
          ))}
        </div>
      </div>
    </section>
  );
}

function BoardToolbar({ users, preferences, fullScreen, cardCount, visibleCount, onPreferencesChange, onToggleFullScreen }: {
  users: User[];
  preferences: BoardPreferences;
  fullScreen: boolean;
  cardCount: number;
  visibleCount: number;
  onPreferencesChange: (patch: BoardPreferencesPatch) => void;
  onToggleFullScreen: () => void;
}) {
  const tr = useT();
  const filterCount = activeBoardFilterCount(preferences.filters);
  return (
    <div className="pane-toolbar board-toolbar">
      <div className="toolbar-title"><Kanban size={17} /> {tr("Board")} <span className="toolbar-count">{visibleCount}/{cardCount}</span></div>
      <div className="board-tools">
        <label className="toolbar-field">
          <SlidersHorizontal size={14} />
          <select value={preferences.groupBy} onChange={(event) => onPreferencesChange({ groupBy: event.target.value as BoardGroupBy })}>
            <option value="phase">{tr("Phase")}</option>
            <option value="status">{tr("Status")}</option>
            <option value="owner">{tr("Owner")}</option>
            <option value="category">{tr("Category")}</option>
            <option value="priority">{tr("Priority")}</option>
          </select>
        </label>
        <label className="toolbar-field search-field">
          <Search size={14} />
          <input placeholder={tr("Search cards")} value={preferences.filters.search} onChange={(event) => onPreferencesChange({ filters: { search: event.target.value } })} />
        </label>
        <label className="toolbar-field">
          <UserCircle size={14} />
          <select value={preferences.filters.ownerId} onChange={(event) => onPreferencesChange({ filters: { ownerId: event.target.value } })}>
            <option value="all">{tr("All owners")}</option>
            <option value="unassigned">{tr("Unassigned")}</option>
            {activeUsers(users).map((item) => <option key={item.id} value={item.id}>{item.display_name}</option>)}
          </select>
        </label>
        <label className="toolbar-field">
          <Filter size={14} />
          <select value={preferences.filters.status} onChange={(event) => onPreferencesChange({ filters: { status: event.target.value } })}>
            <option value="all">{tr("All status")}</option>
            {statuses.map((item) => <option key={item} value={item}>{tr(formatStatus(item))}</option>)}
          </select>
        </label>
        <label className="toolbar-field">
          <span className={`board-category-dot cat-${preferences.filters.category === "all" ? "planning" : preferences.filters.category}`} />
          <select value={preferences.filters.category} onChange={(event) => onPreferencesChange({ filters: { category: event.target.value } })}>
            <option value="all">{tr("All categories")}</option>
            {categories.map((item) => <option key={item} value={item}>{tr(item)}</option>)}
          </select>
        </label>
        <label className="toolbar-field">
          <Clock3 size={14} />
          <select value={preferences.filters.dueWindow} onChange={(event) => onPreferencesChange({ filters: { dueWindow: event.target.value as BoardDueWindow } })}>
            <option value="all">{tr("All dates")}</option>
            <option value="overdue">{tr("Overdue")}</option>
            <option value="next_7">{tr("Next 7 days")}</option>
            <option value="next_30">{tr("Next 30 days")}</option>
            <option value="no_finish">{tr("No finish")}</option>
          </select>
        </label>
        <button className={preferences.filters.criticalOnly ? "filter-toggle active" : "filter-toggle"} title={tr("Critical tasks only")} onClick={() => onPreferencesChange({ filters: { criticalOnly: !preferences.filters.criticalOnly } })}>
          <Flag size={15} />{preferences.filters.criticalOnly ? tr("Critical") : ""}
        </button>
        <button className={preferences.filters.partnerVisibleOnly ? "filter-toggle active" : "filter-toggle"} title={tr("Partner-visible tasks only")} onClick={() => onPreferencesChange({ filters: { partnerVisibleOnly: !preferences.filters.partnerVisibleOnly } })}>
          {preferences.filters.partnerVisibleOnly ? <Eye size={15} /> : <EyeOff size={15} />}{preferences.filters.partnerVisibleOnly ? tr("Partner") : ""}
        </button>
        <Segmented value={preferences.density} options={[["comfortable", tr("Comfort")], ["compact", tr("Compact")]]} onChange={(value) => onPreferencesChange({ density: value as BoardDensity })} />
        <button title={tr(fullScreen ? "Exit board fullscreen" : "Open board fullscreen")} onClick={onToggleFullScreen}>
          {fullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        {filterCount > 0 && <button title={tr("Clear board filters")} onClick={() => onPreferencesChange({ filters: defaultBoardPreferences.filters })}>{tr("Clear")} {filterCount}</button>}
      </div>
    </div>
  );
}

function BoardCard({ task, ownerName, dependencyCount, selected, density, canEdit, dragging, onSelect, onDragStart, onDropBefore, onDragEnd }: {
  task: WorkTask;
  ownerName: string;
  dependencyCount: number;
  selected: boolean;
  density: BoardDensity;
  canEdit: boolean;
  dragging: boolean;
  onSelect: () => void;
  onDragStart: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDropBefore: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
}) {
  const tr = useT();
  return (
    <button
      className={`board-card ${selected ? "selected" : ""} ${dragging ? "dragging" : ""} ${density}`}
      draggable={canEdit}
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => { if (canEdit) event.preventDefault(); }}
      onDrop={onDropBefore}
    >
      <span className={`cat-strip cat-${task.category}`} />
      <span className="board-card-head">
        <span className="board-card-title">{tr(task.title)}</span>
        {canEdit && <Move size={14} className="drag-icon" aria-label={tr("Drag to reorder or move this card")} />}
      </span>
      <span className="board-card-meta">
        <span className={`status-dot ${task.status}`} />
        <span>{tr(formatStatus(task.status))}</span>
        <span>{task.code || "No code"}</span>
      </span>
      <span className="board-card-dates"><CalendarDays size={13} />{task.start_date || tr("No start")} {tr("to")} {task.finish_date || tr("No finish")}</span>
      <span className="board-card-meta">
        <span><UserCircle size={13} />{ownerName}</span>
        <span><Flag size={13} />{tr(task.priority)}</span>
        <span>{task.percent_complete}%</span>
      </span>
      <span className="board-progress"><i style={{ width: `${Math.min(100, task.percent_complete)}%` }} /></span>
      <span className="board-card-badges">
        {dependencyCount > 0 && <span className="board-chip" title={tr("Linked dependencies")}><Link size={12} />{dependencyCount}</span>}
        {task.critical && <span className="board-chip danger">{tr("critical")}</span>}
        {task.send_to_taskplanner && <span className="board-chip" title={tr("Sent to Task Planner")}>{tr("planner")}</span>}
        {!task.partner_visible && <span className="board-chip muted" title={tr("Internal only")}>{tr("internal")}</span>}
      </span>
    </button>
  );
}

function Gantt({ tasks, visibleTasks, dependencies, mode, zoom, selectedTaskId, selectedTaskIds, onSelect, onDependencySelect, dependencyFocus, dependencyMode, canEdit, externalScrollRef, onVerticalScroll, onTaskDateChange }: {
  tasks: WorkTask[];
  visibleTasks: WorkTask[];
  dependencies: Dependency[];
  mode: "wbs" | "category";
  zoom: "month" | "quarter" | "half";
  selectedTaskId: number | null;
  selectedTaskIds: number[];
  onSelect: (id: number, options?: TaskSelectOptions) => void;
  onDependencySelect: (dep: Dependency, predecessorId: number, successorId: number, label: string) => void;
  dependencyFocus: boolean;
  dependencyMode: DependencyMode;
  canEdit: boolean;
  externalScrollRef?: { current: HTMLDivElement | null };
  onVerticalScroll?: () => void;
  onTaskDateChange: (changes: TaskDateChange[]) => Promise<unknown>;
}) {
  const tr = useT();
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const [dragPreview, setDragPreview] = useState<Record<number, { start: string | null; finish: string | null }>>({});
  const scrollAnchorRef = useRef<string | null>(null);
  const panningRef = useRef(false);
  const dated = tasks.filter((task) => task.start_date && task.finish_date);
  const minDate = parseDate(dated.map((task) => task.start_date).sort()[0] || isoToday())!;
  const maxDate = parseDate(dated.map((task) => task.finish_date).sort().at(-1) || isoToday())!;
  const start = addDays(minDate, -30);
  const end = addDays(maxDate, 180);
  const dayWidth = zoom === "month" ? 28 : zoom === "quarter" ? 12 : 6;
  const totalDays = daysBetween(start, end) + 1;
  const width = Math.max(1200, totalDays * dayWidth);
  const rows = mode === "category" ? tasks.filter((task) => task.item_type !== "phase") : visibleTasks;
  const rowHeight = 34;
  const selectedTaskSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);

  const taskById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const rowIndexById = useMemo(() => new Map(rows.map((task, index) => [task.id, index])), [rows]);
  const visibleTaskById = useMemo(() => new Map(rows.map((task) => [task.id, task])), [rows]);
  const focusedDependencyIds = useMemo(() => dependencyPathDependencyIds(dependencies, selectedTaskId), [dependencies, selectedTaskId]);
  const depMap = new Map(dependencies.map((dep) => [dep.successor_id, dep]));
  const headerItems = buildHeaderItems(start, end, dayWidth, zoom);
  const hasDependencyFocus = dependencyFocus && focusedDependencyIds.size > 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    scrollAnchorRef.current = null;
  }, [tasks.length, rows.length, zoom, mode]);

  useEffect(() => {
    const element = scrollRef.current;
    const anchor = scrollAnchorRef.current;
    const anchorDate = parseDate(anchor);
    if (!element || !anchorDate) return;
    element.scrollLeft = Math.max(0, daysBetween(start, anchorDate) * dayWidth);
    scrollAnchorRef.current = null;
  }, [start.toISOString(), dayWidth, width]);

  function captureScrollAnchor() {
    const element = scrollRef.current;
    if (!element) return;
    scrollAnchorRef.current = formatIsoDate(addDays(start, Math.round(element.scrollLeft / dayWidth)));
  }

  function previewFor(task: WorkTask) {
    return dragPreview[task.id] || { start: task.start_date, finish: task.finish_date };
  }

  function dragDeltaFromPixels(pixels: number) {
    return Math.round(pixels / dayWidth);
  }

  function visibleEndpoint(taskId: number) {
    const original = taskById.get(taskId);
    if (!original) return null;
    if (visibleTaskById.has(taskId)) {
      const rowIndex = rowIndexById.get(taskId);
      return rowIndex === undefined ? null : { task: original, original, rowIndex, rolledUp: false };
    }
    let parentId = original.parent_id;
    while (parentId) {
      const parent = taskById.get(parentId);
      if (!parent) break;
      const rowIndex = rowIndexById.get(parent.id);
      if (rowIndex !== undefined) return { task: parent, original, rowIndex, rolledUp: true };
      parentId = parent.parent_id;
    }
    return null;
  }

  function endpointX(task: WorkTask, side: "start" | "finish") {
    const preview = previewFor(task);
    const taskStart = parseDate(preview.start);
    const taskFinish = parseDate(preview.finish);
    if (!taskStart || !taskFinish) return null;
    const left = daysBetween(start, taskStart) * dayWidth;
    const barWidth = Math.max(task.item_type === "milestone" ? 16 : 8, (daysBetween(taskStart, taskFinish) + 1) * dayWidth);
    return side === "start" ? left : left + barWidth;
  }

  const dependencyLines = dependencies.flatMap((dep) => {
    const predecessor = taskById.get(dep.predecessor_id);
    const successor = taskById.get(dep.successor_id);
    if (!predecessor || !successor) return [];
    const source = visibleEndpoint(dep.predecessor_id);
    const target = visibleEndpoint(dep.successor_id);
    if (!source || !target || source.task.id === target.task.id) return [];
    const type = dep.dependency_type || "FS";
    const sourceSide = type[0] === "S" ? "start" : "finish";
    const targetSide = type[1] === "F" ? "finish" : "start";
    const sourceX = endpointX(source.task, sourceSide as "start" | "finish");
    const targetX = endpointX(target.task, targetSide as "start" | "finish");
    if (sourceX === null || targetX === null) return [];
    const sourceY = source.rowIndex * rowHeight + rowHeight / 2;
    const targetY = target.rowIndex * rowHeight + rowHeight / 2;
    const direction = sourceX <= targetX ? 1 : -1;
    const bendX = sourceX + direction * Math.max(18, Math.min(70, Math.abs(targetX - sourceX) / 2));
    const path = `M ${sourceX} ${sourceY} L ${bendX} ${sourceY} L ${bendX} ${targetY} L ${targetX} ${targetY}`;
    const rolledUp = source.rolledUp || target.rolledUp;
    const highlighted = focusedDependencyIds.has(dep.id);
    const selected = selectedTaskSet.has(dep.predecessor_id) || selectedTaskSet.has(dep.successor_id);
    const violated = dependencyIsViolated(dep, predecessor, successor);
    const visibleByMode = dependencyMode === "all" || (dependencyMode === "selected" && (selected || highlighted || violated)) || (hasDependencyFocus && highlighted);
    if (dependencyMode === "off" || !visibleByMode) return [];
    const label = `${tr(predecessor.title)} → ${tr(successor.title)}${rolledUp ? ` (${tr("rolled up")})` : ""}`;
    return [{ dep, path, label, rolledUp, highlighted, selected, violated, predecessorId: source.task.id, successorId: target.task.id }];
  });

  function startPan(event: React.MouseEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest(".bar, .bar-label, .resize-handle, .dependency-chip, .dependency-hit")) return;
    const element = scrollRef.current;
    if (!element) return;
    const initialX = event.clientX;
    const initialScrollLeft = element.scrollLeft;
    let moved = false;
    const move = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - initialX;
      if (Math.abs(delta) < 6 && !moved) return;
      moved = true;
      panningRef.current = true;
      element.scrollLeft = initialScrollLeft - delta;
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      if (moved) window.setTimeout(() => { panningRef.current = false; }, 0);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  function handleWheel(event: React.WheelEvent<HTMLDivElement>) {
    const element = scrollRef.current;
    if (!element) return;
    if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      element.scrollLeft += event.deltaY;
    }
  }

  function startDrag(task: WorkTask, kind: "move" | "start" | "finish", event: React.MouseEvent) {
    if (!canEdit || !task.start_date || !task.finish_date || task.item_type === "phase" || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      onSelect(task.id, { toggle: event.ctrlKey || event.metaKey, range: event.shiftKey });
      panningRef.current = true;
      window.setTimeout(() => { panningRef.current = false; }, 0);
      return;
    }
    const element = scrollRef.current;
    const movingSelection = kind === "move" && selectedTaskSet.has(task.id);
    const dragTasks = movingSelection
      ? rows.filter((item) => selectedTaskSet.has(item.id) && item.start_date && item.finish_date && item.item_type !== "phase")
      : [task];
    if (!dragTasks.length) return;
    onSelect(task.id, movingSelection ? { preserveSelection: true } : undefined);

    const initialX = event.clientX;
    const initialScrollLeft = element?.scrollLeft || 0;
    const threshold = Math.max(6, Math.min(14, dayWidth));
    const edgeZone = 52;
    const edgeStep = Math.max(10, Math.min(36, dayWidth * 3));
    const initialItems = dragTasks.map((item) => ({
      id: item.id,
      originalStart: item.start_date || "",
      originalFinish: item.finish_date || "",
      initialStart: parseDate(item.start_date)!,
      initialFinish: parseDate(item.finish_date)!
    }));
    let latestItems = initialItems.map((item) => ({ id: item.id, start: item.initialStart, finish: item.initialFinish, originalStart: item.originalStart, originalFinish: item.originalFinish }));
    let moved = false;

    const move = (moveEvent: MouseEvent) => {
      if (element) {
        const rect = element.getBoundingClientRect();
        if (moveEvent.clientX > rect.right - edgeZone) element.scrollLeft += edgeStep;
        if (moveEvent.clientX < rect.left + edgeZone) element.scrollLeft = Math.max(0, element.scrollLeft - edgeStep);
      }
      const pixels = (moveEvent.clientX - initialX) + ((element?.scrollLeft || initialScrollLeft) - initialScrollLeft);
      if (Math.abs(pixels) < threshold && !moved) return;
      moved = true;
      panningRef.current = true;
      const delta = dragDeltaFromPixels(pixels);
      const nextPreview: Record<number, { start: string; finish: string }> = {};
      latestItems = initialItems.map((item) => {
        let latestStart = item.initialStart;
        let latestFinish = item.initialFinish;
        if (kind === "move") {
          latestStart = addDays(item.initialStart, delta);
          latestFinish = addDays(item.initialFinish, delta);
        }
        if (kind === "start") {
          latestStart = addDays(item.initialStart, delta);
          if (latestStart > latestFinish) latestStart = latestFinish;
        }
        if (kind === "finish") {
          latestFinish = addDays(item.initialFinish, delta);
          if (latestFinish < latestStart) latestFinish = latestStart;
        }
        const startDate = formatIsoDate(latestStart);
        const finishDate = formatIsoDate(latestFinish);
        nextPreview[item.id] = { start: startDate, finish: finishDate };
        return { id: item.id, start: latestStart, finish: latestFinish, originalStart: item.originalStart, originalFinish: item.originalFinish };
      });
      setDragPreview(nextPreview);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setDragPreview({});
      if (moved) window.setTimeout(() => { panningRef.current = false; }, 0);
      if (!moved) return;
      const changes = latestItems
        .map((item) => ({
          taskId: item.id,
          startDate: formatIsoDate(item.start),
          finishDate: formatIsoDate(item.finish),
          start: item.start,
          finish: item.finish,
          originalStart: item.originalStart,
          originalFinish: item.originalFinish
        }))
        .filter((item) => item.startDate !== item.originalStart || item.finishDate !== item.originalFinish)
        .map((item) => ({
          taskId: item.taskId,
          body: {
            start_date: item.startDate,
            finish_date: item.finishDate,
            duration_days: daysBetween(item.start, item.finish),
            scheduling_mode: "manual" as const
          }
        }));
      if (!changes.length) return;
      captureScrollAnchor();
      onTaskDateChange(changes);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  return (
    <div className="gantt-scroll" ref={scrollRef} onMouseDown={startPan} onWheel={handleWheel} onScroll={onVerticalScroll} title={tr("Pan the timeline by dragging empty space. Shift + mouse wheel scrolls sideways.")}>
      <div className="gantt" style={{ width }}>
        <div className="gantt-header">
          {headerItems.map((item) => (
            <div key={item.key} className={item.weekend ? "weekend" : ""} style={{ width: item.width }}>
              {item.label}
            </div>
          ))}
        </div>
        <div className="gantt-body" style={{ height: rows.length * rowHeight }}>
          {rows.map((task, rowIndex) => {
            const preview = previewFor(task);
            const taskStart = parseDate(preview.start);
            const taskFinish = parseDate(preview.finish);
            const left = taskStart ? daysBetween(start, taskStart) * dayWidth : 0;
            const barWidth = taskStart && taskFinish ? Math.max(task.item_type === "milestone" ? 16 : 8, (daysBetween(taskStart, taskFinish) + 1) * dayWidth) : 0;
            const predecessor = depMap.get(task.id);
            const isPrimarySelected = selectedTaskId === task.id;
            const isMultiSelected = selectedTaskSet.has(task.id);
            return (
              <button
                key={task.id}
                data-task-id={task.id}
                className={`gantt-row ${rowIndex % 2 === 0 ? "row-even" : "row-odd"} ${task.item_type === "phase" ? "phase-row" : ""} ${isPrimarySelected ? "selected" : ""} ${isMultiSelected ? "multi-selected" : ""}`}
                style={{ top: rowIndex * rowHeight, height: rowHeight }}
                onClick={(event) => {
                  if (panningRef.current) {
                    event.preventDefault();
                    return;
                  }
                  onSelect(task.id, { toggle: event.ctrlKey || event.metaKey, range: event.shiftKey });
                }}
              >
                <span className="row-grid" style={{ backgroundSize: `${dayWidth}px 100%` }} />
                {predecessor && <span className="dependency-chip" title={tr("This task has a predecessor dependency")} style={{ left: Math.max(0, left - 22) }}><Link size={11} /></span>}
                {taskStart && taskFinish && (
                  <span
                    className={`bar ${task.item_type} cat-${task.category} ${task.critical ? "critical" : ""} ${task.status} ${isMultiSelected ? "selected-bar" : ""}`}
                    style={{ left, width: barWidth }}
                    title={`${tr(task.title)}: ${task.start_date} ${tr("to")} ${task.finish_date}`}
                    onMouseDown={(event) => startDrag(task, "move", event)}
                  >
                    <span style={{ width: `${Math.min(100, task.percent_complete)}%` }} />
                    {canEdit && task.item_type !== "milestone" && task.item_type !== "phase" && <>
                      <i className="resize-handle left" title={tr("Drag to change start date")} onMouseDown={(event) => startDrag(task, "start", event)} />
                      <i className="resize-handle right" title={tr("Drag to change finish date")} onMouseDown={(event) => startDrag(task, "finish", event)} />
                    </>}
                  </span>
                )}
                {task.baseline?.start_date && task.baseline.finish_date && (
                  <span
                    className="baseline-bar"
                    style={{
                      left: daysBetween(start, parseDate(task.baseline.start_date)!) * dayWidth,
                      width: Math.max(8, (daysBetween(parseDate(task.baseline.start_date)!, parseDate(task.baseline.finish_date)!) + 1) * dayWidth)
                    }}
                  />
                )}
                <span
                  className={`bar-label ${canEdit && task.item_type !== "phase" ? "draggable" : ""}`}
                  style={{ left: left + barWidth + 8 }}
                  title={canEdit && task.item_type !== "phase" ? tr("Drag task label to move dates") : tr(task.title)}
                  onMouseDown={canEdit && task.item_type !== "phase" ? (event) => startDrag(task, "move", event) : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (panningRef.current) {
                      event.preventDefault();
                      return;
                    }
                    onSelect(task.id, { toggle: event.ctrlKey || event.metaKey, range: event.shiftKey });
                  }}
                >
                  {tr(task.title)}
                </span>
              </button>
            );
          })}
          <svg className="dependency-lines" width={width} height={rows.length * rowHeight} viewBox={`0 0 ${width} ${rows.length * rowHeight}`} aria-hidden="true">
            <defs>
              <marker id="dependency-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 8 4 L 0 8 z" />
              </marker>
            </defs>
            {dependencyLines.map((line) => (
              <g
                key={line.dep.id}
                className={`dependency-line ${line.rolledUp ? "rolled-up" : ""} ${line.violated ? "violated" : ""} ${line.selected ? "selected" : ""} ${hasDependencyFocus && !line.highlighted ? "faded" : ""} ${hasDependencyFocus && line.highlighted ? "focused" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onDependencySelect(line.dep, line.predecessorId, line.successorId, line.label);
                }}
              >
                <title>{`${line.label} · ${dependencyLabel(line.dep)}`}</title>
                <path className="dependency-hit" d={line.path} />
                <path className="dependency-path" d={line.path} markerEnd="url(#dependency-arrow)" />
              </g>
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

function buildHeaderItems(start: Date, end: Date, dayWidth: number, zoom: "month" | "quarter" | "half") {
  const items: { key: string; label: string; width: number; weekend?: boolean }[] = [];
  if (zoom === "month") {
    const totalDays = daysBetween(start, end) + 1;
    for (let index = 0; index < totalDays; index += 1) {
      const day = addDays(start, index);
      items.push({
        key: formatIsoDate(day),
        label: day.getDate() === 1 || index === 0 ? day.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : String(day.getDate()),
        width: dayWidth,
        weekend: day.getDay() === 0 || day.getDay() === 6
      });
    }
    return items;
  }
  if (zoom === "quarter") {
    let cursor = new Date(start);
    while (cursor <= end) {
      const days = Math.min(7, daysBetween(cursor, end) + 1);
      items.push({
        key: `${formatIsoDate(cursor)}-week`,
        label: `${cursor.toLocaleDateString(undefined, { month: "short" })} W${weekOfMonth(cursor)}`,
        width: days * dayWidth
      });
      cursor = addDays(cursor, days);
    }
    return items;
  }
  let cursor = new Date(start);
  while (cursor <= end) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const days = Math.min(daysBetween(cursor, monthEnd) + 1, daysBetween(cursor, end) + 1);
    items.push({
      key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
      label: cursor.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
      width: Math.max(48, days * dayWidth)
    });
    cursor = addDays(cursor, days);
  }
  return items;
}

function TaskInspector({ task, users, calendars, canEdit, role, busy, onSave, onPropose }: {
  task: WorkTask | null;
  users: User[];
  calendars: Calendar[];
  canEdit: boolean;
  role: Role;
  busy: boolean;
  onSave: (taskId: number, body: Partial<WorkTask>) => Promise<unknown>;
  onPropose: (body: { task_id: number; field_changes: Record<string, unknown>; reason?: string }) => Promise<unknown>;
}) {
  const tr = useT();
  const [form, setForm] = useState<Partial<WorkTask>>({});
  const [reason, setReason] = useState("");

  useEffect(() => {
    setForm(task || {});
    setReason("");
  }, [
    task?.id,
    task?.title,
    task?.description,
    task?.status,
    task?.category,
    task?.priority,
    task?.owner_user_id,
    task?.calendar_id,
    task?.start_date,
    task?.finish_date,
    task?.duration_days,
    task?.scheduling_mode,
    task?.percent_complete,
    task?.partner_visible,
    task?.send_to_taskplanner,
    task?.budget_cost,
    task?.actual_cost
  ]);

  if (!task) return <aside className="inspector"><div className="empty-state">{tr("No task selected")}</div></aside>;
  const currentTask = task;

  const update = (key: keyof WorkTask, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  const formStartDate = String(form.start_date ?? task.start_date ?? "");
  const formFinishDate = String(form.finish_date ?? task.finish_date ?? "");
  const storedSpan = Math.max(0, Number(form.duration_days ?? task.duration_days ?? 0));
  const currentSpan = formStartDate && formFinishDate ? daysBetween(parseDate(formStartDate)!, parseDate(formFinishDate)!) : storedSpan;
  const visibleDuration = inclusiveDurationDays(formStartDate || null, formFinishDate || null, storedSpan);
  function updateStart(value: string) {
    const nextStart = parseDate(value);
    const nextFinish = nextStart ? formatIsoDate(addDays(nextStart, currentSpan)) : form.finish_date;
    setForm((current) => ({ ...current, start_date: value, finish_date: nextFinish, duration_days: currentSpan, scheduling_mode: "manual" }));
  }
  function updateFinish(value: string) {
    const start = parseDate(String(form.start_date || currentTask.start_date || ""));
    const finish = parseDate(value);
    const safeFinish = start && finish && finish < start ? start : finish;
    setForm((current) => ({
      ...current,
      finish_date: safeFinish ? formatIsoDate(safeFinish) : value,
      duration_days: start && safeFinish ? daysBetween(start, safeFinish) : current.duration_days,
      scheduling_mode: "manual"
    }));
  }
  function updateDuration(value: number) {
    const span = storedDurationFromInclusive(value);
    setForm((current) => {
      const start = parseDate(String(current.start_date || currentTask.start_date || ""));
      return {
        ...current,
        duration_days: span,
        finish_date: start ? formatIsoDate(addDays(start, span)) : current.finish_date,
        scheduling_mode: "manual"
      };
    });
  }
  const weekendCalendar = calendars.find((calendar) => calendar.working_weekdays.split(",").filter(Boolean).length >= 7) || calendars.find((calendar) => calendar.name.toLowerCase().includes("weekend"));
  const weekdayCalendar = calendars.find((calendar) => calendar.working_weekdays === "0,1,2,3,4") || calendars.find((calendar) => !calendar.name.toLowerCase().includes("weekend"));
  const selectedCalendar = calendars.find((calendar) => calendar.id === (form.calendar_id || task.calendar_id));
  const includesWeekends = selectedCalendar ? selectedCalendar.working_weekdays.split(",").filter(Boolean).length >= 7 : false;
  const proposalChanges = {
    status: form.status,
    start_date: form.start_date,
    finish_date: form.finish_date,
    duration_days: Number(form.duration_days || task.duration_days),
    percent_complete: Number(form.percent_complete || 0),
    description: form.description
  };

  return (
    <aside className="inspector">
      <div className="inspector-head">
        <div>
          <div className="eyebrow">{task.code} · {task.item_type}</div>
          <h2>{tr(task.title)}</h2>
        </div>
        {task.critical && <span className="critical-pill">{tr("critical")}</span>}
      </div>
      <div className="form-grid">
        <label>{tr("Title")}<input disabled={!canEdit} value={form.title || ""} onChange={(event) => update("title", event.target.value)} /><TranslationPreview value={String(form.title || "")} /></label>
        <label>{tr("Status")}<select disabled={!canEdit && role !== "partner"} value={form.status || "not_started"} onChange={(event) => update("status", event.target.value)}>{statuses.map((item) => <option key={item} value={item}>{tr(formatStatus(item))}</option>)}</select></label>
        <label>{tr("Category")}<select disabled={!canEdit} value={form.category || "planning"} onChange={(event) => update("category", event.target.value)}>{categories.map((item) => <option key={item} value={item}>{tr(item)}</option>)}</select></label>
        <label>{tr("Priority")}<select disabled={!canEdit} value={form.priority || "medium"} onChange={(event) => update("priority", event.target.value)}>{priorities.map((item) => <option key={item} value={item}>{tr(item)}</option>)}</select></label>
        <label>{tr("Start")}<input disabled={!canEdit && role !== "partner"} type="date" value={form.start_date || ""} onChange={(event) => updateStart(event.target.value)} /></label>
        <label>{tr("Finish")}<input disabled={!canEdit && role !== "partner"} type="date" value={form.finish_date || ""} onChange={(event) => updateFinish(event.target.value)} /></label>
        <label>{tr("Duration")}<input disabled={!canEdit && role !== "partner"} type="number" min="1" value={visibleDuration} onChange={(event) => updateDuration(Number(event.target.value))} /></label>
        <label>{tr("Complete %")}<input disabled={!canEdit && role !== "partner"} type="number" min="0" max="100" value={form.percent_complete ?? 0} onChange={(event) => update("percent_complete", Number(event.target.value))} /></label>
        <label>{tr("Scheduling")}<select disabled={!canEdit} value={form.scheduling_mode || "auto"} onChange={(event) => update("scheduling_mode", event.target.value)}><option value="auto">{tr("auto")}</option><option value="manual">{tr("manual")}</option></select></label>
        <label>{tr("Owner")}<select disabled={!canEdit} value={form.owner_user_id || ""} onChange={(event) => update("owner_user_id", event.target.value ? Number(event.target.value) : null)}><option value="">{tr("Unassigned")}</option>{activeUsers(users).map((item) => <option key={item.id} value={item.id}>{item.display_name}</option>)}</select></label>
        <label className="wide">{tr("Description")}<textarea disabled={!canEdit && role !== "partner"} value={form.description || ""} onChange={(event) => update("description", event.target.value)} /><TranslationPreview value={String(form.description || "")} /></label>
        <label className="check-row"><input disabled={!canEdit} type="checkbox" checked={!!form.partner_visible} onChange={(event) => update("partner_visible", event.target.checked)} />{tr("Partner visible")}</label>
        <label className="check-row"><input disabled={!canEdit} type="checkbox" checked={!!form.send_to_taskplanner} onChange={(event) => update("send_to_taskplanner", event.target.checked)} />{tr("Task Planner")}</label>
        <label className="check-row wide" title={tr("Include weekends uses elapsed calendar days; excluding weekends uses normal workdays.")}><input disabled={!canEdit || !weekendCalendar || !weekdayCalendar} type="checkbox" checked={includesWeekends} onChange={(event) => update("calendar_id", event.target.checked ? weekendCalendar?.id : weekdayCalendar?.id)} />{tr("Include weekends / elapsed time")}</label>
      </div>
      {task.delay_impact && <div className="impact-note"><AlertTriangle size={15} />{tr(task.delay_impact)}</div>}
      {canEdit ? (
        <button disabled={busy} onClick={() => onSave(task.id, form)}><Save size={17} /> {tr("Save")}</button>
      ) : (
        <form className="proposal-box" onSubmit={(event) => { event.preventDefault(); onPropose({ task_id: task.id, field_changes: proposalChanges, reason }); }}>
          <label>{tr("Reason")}<textarea value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          <button disabled={busy} type="submit"><Send size={17} /> {tr("Propose")}</button>
        </form>
      )}
    </aside>
  );
}

function OpsTab({ tab, workspace, canEdit, isAdmin, busy, onAction, api, projectId }: {
  tab: Tab;
  workspace: Workspace;
  canEdit: boolean;
  isAdmin: boolean;
  busy: boolean;
  onAction: (action: () => Promise<unknown>, success: string) => Promise<void>;
  api: <T>(path: string, options?: RequestInit) => Promise<T>;
  projectId: number | null;
}) {
  const tr = useT();
  if (tab === "risks") {
    return <RiskDecisionTable items={workspace.risks} type="risk" canEdit={canEdit} busy={busy} onSubmit={(body) => onAction(() => api(`/api/projects/${projectId}/risks`, { method: "POST", body: JSON.stringify(body) }), "Risk added")} />;
  }
  if (tab === "decisions") {
    return <RiskDecisionTable items={workspace.decisions} type="decision" canEdit={canEdit} busy={busy} onSubmit={(body) => onAction(() => api(`/api/projects/${projectId}/decisions`, { method: "POST", body: JSON.stringify(body) }), "Decision added")} />;
  }
  if (tab === "proposals") {
    return <ProposalTable proposals={workspace.proposals} tasks={workspace.tasks} busy={busy} canEdit={canEdit} onReview={(id, approved) => onAction(() => api(`/api/change-proposals/${id}/${approved ? "approve" : "reject"}`, { method: "POST", body: JSON.stringify({ review_note: approved ? "Approved in workspace" : "Rejected in workspace" }) }), approved ? "Proposal approved" : "Proposal rejected")} />;
  }
  if (tab === "resources") {
    return <ResourceTable resources={workspace.resources} assignments={workspace.assignments} tasks={workspace.tasks} />;
  }
  if (tab === "baselines") {
    return <BaselinePanel baselines={workspace.baselines} canEdit={canEdit} busy={busy} onCapture={(name) => onAction(() => api(`/api/projects/${projectId}/baselines`, { method: "POST", body: JSON.stringify({ name }) }), "Baseline captured")} />;
  }
  if (tab === "taskplanner") {
    const marked = workspace.tasks.filter((task) => task.send_to_taskplanner);
    return <TaskPlannerPanel tasks={marked} canEdit={canEdit} busy={busy} onSync={() => onAction(() => api(`/api/projects/${projectId}/taskplanner/sync`, { method: "POST" }), "Task Planner sync completed")} />;
  }
  return <PeoplePanel users={workspace.users} isAdmin={isAdmin} busy={busy} onCreate={(body) => onAction(() => api("/api/users", { method: "POST", body: JSON.stringify(body) }), "User created")} />;
}

function RiskDecisionTable({ items, type, canEdit, busy, onSubmit }: { items: any[]; type: "risk" | "decision"; canEdit: boolean; busy: boolean; onSubmit: (body: Record<string, unknown>) => Promise<void> }) {
  const tr = useT();
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  return (
    <div className="ops-content">
      {canEdit && (
        <form className="inline-form" onSubmit={(event) => { event.preventDefault(); onSubmit(type === "risk" ? { title, description: detail, severity: "high", likelihood: "medium" } : { title, context: detail }); setTitle(""); setDetail(""); }}>
          <input placeholder={`${tr(type)} ${tr("title")}`} value={title} onChange={(event) => setTitle(event.target.value)} />
          <input placeholder={tr("detail")} value={detail} onChange={(event) => setDetail(event.target.value)} />
          <button disabled={busy || !title}><Plus size={16} /></button>
        </form>
      )}
      <div className="data-table">
        <div className="table-row table-head"><span>{tr("Title")}</span><span>{tr("Status")}</span><span>{tr("Detail")}</span></div>
        {items.map((item) => (
          <div key={item.id} className="table-row"><span>{tr(item.title) || item.title}</span><span>{tr(formatStatus(item.status))}</span><span>{item.description || item.context || item.decision || ""}<TranslationPreview value={item.description || item.context || item.decision || ""} /></span></div>
        ))}
      </div>
    </div>
  );
}

function ProposalTable({ proposals, tasks, busy, canEdit, onReview }: { proposals: Proposal[]; tasks: WorkTask[]; busy: boolean; canEdit: boolean; onReview: (id: number, approved: boolean) => Promise<void> }) {
  const tr = useT();
  return (
    <div className="data-table">
      <div className="table-row proposal-row table-head"><span>{tr("Task")}</span><span>{tr("Changes")}</span><span>{tr("Status")}</span><span>{tr("Review")}</span></div>
      {proposals.map((proposal) => (
        <div key={proposal.id} className="table-row proposal-row">
          <span>{tr(tasks.find((task) => task.id === proposal.task_id)?.title) || proposal.task_id}</span>
          <span>{Object.entries(proposal.field_changes).map(([key, value]) => `${key}: ${value}`).join(", ")}<TranslationPreview value={Object.entries(proposal.field_changes).map(([key, value]) => `${key}: ${value}`).join(", ")} /></span>
          <span>{tr(proposal.status)}</span>
          <span className="button-pair">
            {canEdit && proposal.status === "pending" && <>
              <button disabled={busy} onClick={() => onReview(proposal.id, true)}><CheckCircle2 size={16} /></button>
              <button disabled={busy} onClick={() => onReview(proposal.id, false)}><XCircle size={16} /></button>
            </>}
          </span>
        </div>
      ))}
    </div>
  );
}

function ResourceTable({ resources, assignments, tasks }: { resources: Resource[]; assignments: Workspace["assignments"]; tasks: WorkTask[] }) {
  const tr = useT();
  return (
    <div className="data-table">
      <div className="table-row resource-row table-head"><span>{tr("Resource")}</span><span>{tr("Capacity")}</span><span>{tr("Assignments")}</span></div>
      {resources.map((resource) => {
        const taskNames = assignments.filter((item) => item.resource_id === resource.id).map((item) => tr(tasks.find((task) => task.id === item.task_id)?.title)).filter(Boolean);
        return <div key={resource.id} className="table-row resource-row"><span>{resource.name}</span><span>{resource.capacity_percent}%</span><span>{taskNames.join(", ") || tr("None")}</span></div>;
      })}
    </div>
  );
}

function BaselinePanel({ baselines, canEdit, busy, onCapture }: { baselines: BaselineRecord[]; canEdit: boolean; busy: boolean; onCapture: (name: string) => Promise<void> }) {
  const tr = useT();
  const [name, setName] = useState(`Baseline ${isoToday()}`);
  return (
    <div className="ops-content">
      {canEdit && <form className="inline-form" onSubmit={(event) => { event.preventDefault(); onCapture(name); }}><input value={name} onChange={(event) => setName(event.target.value)} /><button disabled={busy}><Baseline size={16} /></button></form>}
      <div className="data-table">
        <div className="table-row table-head"><span>{tr("Name")}</span><span>{tr("Captured")}</span><span>{tr("Items")}</span></div>
        {baselines.map((baseline) => <div key={baseline.id} className="table-row"><span>{baseline.name}</span><span>{baseline.captured_at}</span><span>{baseline.item_count}</span></div>)}
      </div>
    </div>
  );
}

function TaskPlannerPanel({ tasks, canEdit, busy, onSync }: { tasks: WorkTask[]; canEdit: boolean; busy: boolean; onSync: () => Promise<void> }) {
  const tr = useT();
  return (
    <div className="ops-content">
      {canEdit && <button disabled={busy} onClick={onSync}><Send size={16} /> {tr("Sync marked follow-ups")}</button>}
      <div className="data-table">
        <div className="table-row table-head"><span>{tr("Follow-up")}</span><span>{tr("Due")}</span><span>{tr("Sync")}</span></div>
        {tasks.map((task) => <div key={task.id} className="table-row"><span>{tr(task.title)}</span><span>{task.finish_date}</span><span>{tr(task.taskplanner_sync?.last_status || "not synced")}</span></div>)}
      </div>
    </div>
  );
}

function PeoplePanel({ users, isAdmin, busy, onCreate }: { users: User[]; isAdmin: boolean; busy: boolean; onCreate: (body: Record<string, unknown>) => Promise<void> }) {
  const tr = useT();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("partner");
  const [password, setPassword] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    onCreate({ username, display_name: displayName, role, password });
    setUsername("");
    setDisplayName("");
    setPassword("");
  }
  return (
    <div className="ops-content">
      {isAdmin && (
        <form className="inline-form people-form" onSubmit={submit}>
          <input placeholder={tr("Username")} value={username} onChange={(event) => setUsername(event.target.value)} />
          <input placeholder={tr("display name")} value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          <select value={role} onChange={(event) => setRole(event.target.value as Role)}><option value="partner">{tr("partner")}</option><option value="editor">{tr("editor")}</option><option value="admin">{tr("admin")}</option></select>
          <input placeholder={tr("Password")} type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          <button disabled={busy || !username || !displayName || password.length < 8}><UserPlus size={16} /></button>
        </form>
      )}
      <div className="data-table">
        <div className="table-row table-head"><span>{tr("Name")}</span><span>{tr("Username")}</span><span>{tr("Role")}</span></div>
        {users.map((item) => <div key={item.id} className="table-row"><span>{item.display_name}</span><span>{item.username}</span><span>{tr(item.role)}</span></div>)}
      </div>
    </div>
  );
}
